import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, TextInput, ActivityIndicator } from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { Input, Button } from '../design-system';
import { RichTextEditor } from '../design-system/RichTextEditor';
import { BusinessUserPicker } from '../BusinessUserPicker';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { apiService, DataListColumn } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { FlowNode, FlowNodeConfig, FlowButton, FlowAgentExit, genId, getAvailableVariables, CHECK_LANGUAGE_OPTIONS, AGENT_EXIT_KINDS, aiStepToAgent } from './flow-types';
import { ConditionGroupsEditor } from './ConditionGroupsEditor';
import { InterpolatableInput } from './InterpolatableInput';
import { AssetCard } from '../WhatsAppMessageForm/components/WhatsAppMessagePreviewEditable';
import { VariablePicker } from './VariablePicker';
import { NodeMediaPickerField } from './NodeMediaPickerField';
import { useThemedStyles } from '../../hooks/useThemedStyles';

interface NodeConfigFormProps {
  node: FlowNode;
  otherNodes: FlowNode[]; // for picking a nextNodeId / button target
  onChange: (patch: Partial<FlowNode>) => void;
  errors: Record<string, string>;
}

/** Renders the config form for whichever node type is selected. One switch, not 13 files —
 * each node type's fields are simple enough that splitting further would mean more
 * boilerplate (props, imports) than actual form logic. */
export const NodeConfigForm: React.FC<NodeConfigFormProps> = ({ node, otherNodes, onChange, errors }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const cfg = node.config || {};
  const updateConfig = (patch: Partial<FlowNodeConfig>) => onChange({ config: { ...cfg, ...patch } });
  // Re-derived fresh on every render (cheap for flow-sized node counts) so the picker updates
  // live as the business edits ANOTHER node's saveAs/saveResultAs in the same session.
  const availableVariables = getAvailableVariables(otherNodes);

  const [nodePickerFor, setNodePickerFor] = useState<string | null>(null); // edge key currently picking a target
  const [datasetPickerOpen, setDatasetPickerOpen] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [webhookPickerOpen, setWebhookPickerOpen] = useState(false);
  const [calendarPickerOpen, setCalendarPickerOpen] = useState(false);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [departmentPickerOpen, setDepartmentPickerOpen] = useState(false);
  const [contextFieldPickerOpen, setContextFieldPickerOpen] = useState(false);
  const [callFlowPickerOpen, setCallFlowPickerOpen] = useState(false);
  const [dataListPickerOpen, setDataListPickerOpen] = useState(false);
  const [otpTemplatePickerOpen, setOtpTemplatePickerOpen] = useState(false);
  const [waTemplatePickerOpen, setWaTemplatePickerOpen] = useState(false);
  // params: how many {{n}} values; sections: which part each {{n}} fills; headerMedia: an image / video / document / location header.
  const [waTemplates, setWaTemplates] = useState<Array<{ _id: string; templateName: string; language: string; category?: string; params: number; sections: Record<number, string>; headerMedia?: string }>>([]);
  const [listColumnPickerFor, setListColumnPickerFor] = useState<string | null>(null);

  const [datasets, setDatasets] = useState<Array<{ _id: string; name: string }>>([]);
  const [products, setProducts] = useState<Array<{ _id: string; name: string }>>([]);
  const [catalogs, setCatalogs] = useState<Array<{ _id: string; name: string }>>([]);
  const [webhooks, setWebhooks] = useState<Array<{ _id: string; title: string }>>([]);
  const [calendars, setCalendars] = useState<Array<{ _id: string; name: string }>>([]);
  const [callableFlows, setCallableFlows] = useState<Array<{ _id: string; name: string }>>([]);
  const [dataLists, setDataLists] = useState<Array<{ _id: string; name: string }>>([]);
  const [otpTemplates, setOtpTemplates] = useState<Array<{ _id: string; templateName: string; language: string }>>([]);
  const [selectedListColumns, setSelectedListColumns] = useState<DataListColumn[]>([]);
  const [contactLists, setContactLists] = useState<Array<{ _id: string; name: string }>>([]);
  const [contactListColumns, setContactListColumns] = useState<DataListColumn[]>([]);
  const [contactListPickerOpen, setContactListPickerOpen] = useState(false);
  const [customColumnPickerFor, setCustomColumnPickerFor] = useState<string | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  // send_bulk_whatsapp: every data list (any kind) and the contacts lists, plus its pickers.
  const [bulkLists, setBulkLists] = useState<Array<{ _id: string; name: string; kind: string }>>([]);
  const [bulkContactLists, setBulkContactLists] = useState<Array<{ _id: string; name: string }>>([]);
  const [bulkPicker, setBulkPicker] = useState<null | 'list' | 'contactList' | 'phone' | 'name' | 'user' | 'department'>(null);
  const [asgPicker, setAsgPicker] = useState<null | 'users' | 'departments' | 'toUser' | 'toDepartment'>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoadingOptions(true);
        if (node.type === 'send_dataset' && datasets.length === 0) {
          const res = await apiService.getQuickReplySets({ limit: 1000, skip: 0 });
          if (res.statusCode === 1000 && res.data?.quickReplySets) {
            setDatasets(res.data.quickReplySets.map((s: any) => ({ _id: s._id, name: s.name || 'Unnamed Set' })));
          }
        }
        if (node.type === 'show_catalog' && catalogs.length === 0) {
          const res = await apiService.getCatalogues();
          if (res.statusCode === 1000 && res.data?.catalogues) {
            setCatalogs(res.data.catalogues.map((c: any) => ({ _id: c._id, name: c.name || 'Unnamed Catalog' })));
          }
        }
        if (node.type === 'show_product' && products.length === 0) {
          const catRes = await apiService.getCatalogues();
          if (catRes.statusCode === 1000 && catRes.data?.catalogues) {
            const all: Array<{ _id: string; name: string }> = [];
            for (const cat of catRes.data.catalogues) {
              const prodRes = await apiService.getProductsByCatalogue(cat._id, { limit: 1000, skip: 0 });
              if (prodRes.statusCode === 1000 && prodRes.data?.products) {
                all.push(...prodRes.data.products.map((p: any) => ({ _id: p._id, name: p.name || p.title || 'Unnamed Product' })));
              }
            }
            setProducts(all);
          }
        }
        if (node.type === 'webhook' && webhooks.length === 0) {
          const res = await apiService.listWebhooks();
          if (res.statusCode === 1000 && Array.isArray(res.data)) {
            setWebhooks(res.data.map((w: any) => ({ _id: w._id, title: w.title || 'Untitled Webhook' })));
          }
        }
        if (node.type === 'create_calendar_event' && calendars.length === 0) {
          const res = await apiService.getCalendars({ isActive: true });
          const arr: any[] = Array.isArray(res.data) ? res.data : (res.data as any)?.calendars || [];
          setCalendars(arr.map((c: any) => ({ _id: c._id, name: c.name || 'Unnamed Calendar' })));
        }
        if (node.type === 'send_bulk_whatsapp' && bulkLists.length === 0) {
          const res = await apiService.getDataLists({ limit: 1000 });
          if (res.statusCode === 1000 && Array.isArray(res.data)) {
            const kind = (l: any) => (l.dataSource === 'webhook' ? 'webhook' : l.columnsSource === 'system' ? (l.systemModule || 'system') : 'static');
            setBulkLists(res.data.map((l) => ({ _id: l._id, name: l.name || 'Unnamed List', kind: kind(l) })));
            setBulkContactLists(res.data.filter((l) => l.columnsSource === 'system' && l.systemModule === 'contacts').map((l) => ({ _id: l._id, name: l.name || 'Contacts list' })));
          }
        }
        if ((node.type === 'send_template' || node.type === 'send_bulk_whatsapp') && waTemplates.length === 0) {
          const res = await apiService.getWhatsAppCustomTemplates({ approvedOnly: true });
          const arr: any[] = Array.isArray(res.data?.templates) ? res.data.templates : [];
          // Every {{n}} the template takes — header, body or button — so the form offers an input
          // for each, labelled by where it goes; and whether its header is a media file.
          const sectionsOf = (t: any): Record<number, string> => {
            const out: Record<number, string> = {};
            for (const p of t.parameters || []) if (p?.position) out[Number(p.position)] = p.section || 'body';
            const scan = (text: any, section: string) => (String(text || '').match(/\{\{\s*(\d+)\s*\}\}/g) || []).forEach((m) => { const n = Number(m.replace(/\D/g, '')); if (!out[n]) out[n] = section; });
            scan(t.templateHeader, 'header');
            scan(t.templateBody || (t.components || []).find((c: any) => String(c.type).toUpperCase() === 'BODY')?.text || t.body || t.content, 'body');
            (t.buttons || []).forEach((b: any) => scan(b.url, 'button'));
            return out;
          };
          setWaTemplates(arr.map((t: any) => {
            const sections = sectionsOf(t);
            const positions = Object.keys(sections).map(Number);
            const media = t.headerAsset?.type && t.headerAsset.type !== 'text' ? t.headerAsset.type : undefined;
            return { _id: t._id, templateName: t.templateName, language: t.language, category: t.category, params: positions.length ? Math.max(...positions) : 0, sections, headerMedia: media };
          }));
        }
        if ((node.type === 'write_data_list' || node.type === 'add_to_list' || node.type === 'remove_from_list') && dataLists.length === 0) {
          const res = await apiService.getDataLists({ limit: 1000 });
          if (res.statusCode === 1000 && Array.isArray(res.data)) {
            setDataLists(res.data.map((l) => ({ _id: l._id, name: l.name || 'Unnamed List' })));
          }
        }
        if (node.type === 'update_contact' && contactLists.length === 0) {
          // The same Contacts data lists whose custom fields the contact screen edits.
          const res = await apiService.getDataLists({ systemModule: 'contacts', limit: 1000 });
          if (res.statusCode === 1000 && Array.isArray(res.data)) {
            setContactLists(res.data.filter((l: any) => (l.customColumns || []).length > 0).map((l) => ({ _id: l._id, name: l.name || 'Contacts list' })));
          }
        }
        if (node.type === 'call_flow' && callableFlows.length === 0) {
          const res = await apiService.getFlows({ isActive: true, limit: 1000 });
          const arr: any[] = Array.isArray(res.data) ? res.data : (res.data as any)?.items || [];
          // Self-calls and deeper transitive cycles (this flow's own id isn't known here — the
          // builder loads a flow's nodes without threading its own _id into this form) are
          // caught server-side by flow.service.ts's cross-flow cycle guard at save time.
          setCallableFlows(arr.map((f: any) => ({ _id: f._id, name: f.name || 'Unnamed Flow' })));
        }
        if (node.type === 'send_otp' && otpTemplates.length === 0) {
          const res = await apiService.getWhatsAppCustomTemplates({ approvedOnly: true });
          const arr: any[] = Array.isArray(res.data?.templates) ? res.data.templates : [];
          // AUTHENTICATION is Meta's own category value for OTP-class templates — a real Meta
          // OTP UX (copy-code button, built-in expiry text) can only come from a template
          // submitted and approved under this category, never a MARKETING/UTILITY one.
          setOtpTemplates(
            arr
              .filter((t: any) => t.category === 'AUTHENTICATION')
              .map((t: any) => ({ _id: t._id, templateName: t.templateName, language: t.language }))
          );
        }
      } catch (error) {
        console.error('Error loading node config options:', error);
      } finally {
        setLoadingOptions(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.type]);

  // AI Agent step → "Act as": the business's AI agents.
  const [aiAgents, setAiAgents] = useState<Array<{ id: string; name: string; status: string; paused: boolean; isDefault?: boolean }>>([]);
  // A new AI step starts with the default agent (else the first active one) already chosen.
  useEffect(() => {
    if ((node.type !== 'ai_agent' && node.type !== 'ai_step') || cfg.agentActAs || !aiAgents.length) return;
    const ready = aiAgents.filter((a) => a.status === 'active' && !a.paused);
    const pick = ready.find((a) => a.isDefault) || ready[0];
    if (pick) updateConfig({ agentActAs: pick.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiAgents, node.id]);
  useEffect(() => {
    if (node.type !== 'ai_agent' && node.type !== 'ai_step') return;
    let cancelled = false;
    apiService.getAiAgents().then((res) => { if (!cancelled && res.statusCode === 1000 && res.data) setAiAgents(res.data.agents); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [node.type]);

  // AI Agent / AI Step: the AI agent it speaks as — required; its name is what the customer's
  // conversation, the cart and the reports show.
  const renderActAs = () => (
    <View style={styles.field}>
      <Text style={styles.label}>Speaks as (AI agent) *</Text>
      <View style={styles.chipRow}>
        {[...aiAgents, { id: 'assigned', name: "The contact's own AI agent", status: 'active', paused: false }].map((a) => {
          const on = cfg.agentActAs === a.id;
          const off = a.status === 'suspended' || a.paused;
          return (
            <TouchableOpacity key={a.id} style={[styles.chip, on && styles.actionTypeOptionActive]} onPress={() => updateConfig({ agentActAs: a.id })}>
              <Text style={[styles.helperText, { marginTop: 0 }, on && { color: colors.primary }]}>{a.name}{off ? ' (off)' : ''}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
      {errors.agentActAs && <Text style={styles.errorText}>{errors.agentActAs}</Text>}
      <Text style={[styles.label, { marginTop: spacing.sm }]}>Show products as</Text>
      <View style={styles.chipRow}>
        {[{ id: '', label: "The agent's setting (store page by default)" }, { id: 'page', label: 'Store page' }, { id: 'chat', label: 'In-chat list' }].map((o) => {
          const on = (cfg.agentPresentation || '') === o.id;
          return (
            <TouchableOpacity key={o.id || 'agent'} style={[styles.chip, on && styles.actionTypeOptionActive]} onPress={() => updateConfig({ agentPresentation: (o.id || undefined) as any })}>
              <Text style={[styles.helperText, { marginTop: 0 }, on && { color: colors.primary }]}>{o.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <Text style={styles.helperText}>{aiAgents.length
        ? 'Its name, persona, products and language are used, and its messages, orders and quotations are recorded under it. If it is suspended or paused, the default AI agent answers instead.'
        : 'No AI agents yet — they come with the subscription (Team → AI agents).'}</Text>
    </View>
  );

  // Show Product / Show Catalog: a fixed pick, or the one the conversation names — typically what
  // an AI Agent chose ({{agent.product.id}} after its "Shows a product" exit), any variable, an id or a name.
  const renderShowSource = (kind: 'product' | 'catalog') => {
    const isProduct = kind === 'product';
    const ref = isProduct ? cfg.productRef : cfg.catalogRef;
    const dynamic = ref !== undefined;
    const fallback = isProduct ? '{{agent.product.id}}' : '{{agent.catalog.id}}';
    const pickedName = isProduct
      ? (cfg.productId ? products.find((p) => p._id === cfg.productId)?.name || 'Product selected' : 'Select product')
      : (cfg.catalogId ? catalogs.find((c) => c._id === cfg.catalogId)?.name || 'Catalog selected' : 'Select catalog');
    return (
      <View style={styles.field}>
        <Text style={styles.label}>{isProduct ? 'Product *' : 'Catalog *'}</Text>
        <View style={styles.chipRow}>
          {[{ id: 'fixed', label: isProduct ? 'Pick a product' : 'Pick a catalog' }, { id: 'dynamic', label: 'From the conversation' }].map((o) => {
            const on = (o.id === 'dynamic') === dynamic;
            return (
              <TouchableOpacity key={o.id} style={[styles.chip, on && styles.actionTypeOptionActive]}
                onPress={() => updateConfig(isProduct
                  ? { productRef: o.id === 'dynamic' ? (cfg.productRef ?? fallback) : undefined }
                  : { catalogRef: o.id === 'dynamic' ? (cfg.catalogRef ?? fallback) : undefined })}>
                <Text style={[styles.helperText, { marginTop: 0 }, on && { color: colors.primary }]}>{o.label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
        {dynamic ? (
          <>
            <InterpolatableInput
              label={isProduct ? 'Product id or name' : 'Catalog id or name'}
              value={ref || ''}
              onChangeText={(text) => updateConfig(isProduct ? { productRef: text } : { catalogRef: text })}
              placeholder={fallback}
              size="sm"
              variables={availableVariables}
            />
            <Text style={styles.helperText}>{isProduct
              ? 'After an AI Agent\'s "Shows a product" exit use {{agent.product.id}}. Any variable holding a product id or name works; if nothing matches, the step fails and the flow takes its error path.'
              : 'After an AI Agent\'s "Shows a catalogue" exit use {{agent.catalog.id}}. Any variable holding a catalog id or name works; if nothing matches, the step fails and the flow takes its error path.'}</Text>
          </>
        ) : (
          <TouchableOpacity style={styles.selectButton} onPress={() => (isProduct ? setProductPickerOpen(true) : setCatalogPickerOpen(true))}>
            <Text style={[styles.selectButtonText, !(isProduct ? cfg.productId : cfg.catalogId) && styles.selectButtonPlaceholder]}>{pickedName}</Text>
            <Ionicons name="chevron-down" size={20} color={colors.textLight} />
          </TouchableOpacity>
        )}
        {(isProduct ? errors.productId : errors.catalogId) && <Text style={styles.errorText}>{isProduct ? errors.productId : errors.catalogId}</Text>}
      </View>
    );
  };

  // Load the picked Data List's columns so the value-mapping / match-criteria rows can offer
  // a real column dropdown (resolved by DataListColumn.id) instead of free-text field names.
  useEffect(() => {
    const listId = node.type === 'send_bulk_whatsapp' ? cfg.bulkDataListId : cfg.dataListId;
    if ((node.type !== 'write_data_list' && node.type !== 'add_to_list' && node.type !== 'send_bulk_whatsapp') || !listId) {
      setSelectedListColumns([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getDataList(listId);
        if (!cancelled && res.statusCode === 1000 && res.data) {
          setSelectedListColumns([...res.data.columns, ...(node.type === 'send_bulk_whatsapp' ? (res.data as any).customColumns || [] : [])].sort((a, b) => a.order - b.order));
        }
      } catch (error) {
        console.error('Error loading data list columns:', error);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.type, cfg.dataListId, cfg.bulkDataListId]);

  // The picked Contacts list's custom fields, for update_contact's custom mode.
  useEffect(() => {
    if (node.type !== 'update_contact' || !cfg.customFieldListId) {
      setContactListColumns([]);
      return;
    }
    let cancelled = false;
    apiService.getDataList(cfg.customFieldListId).then((res) => {
      if (!cancelled && res.statusCode === 1000 && res.data) setContactListColumns([...(res.data.customColumns || [])].sort((a, b) => a.order - b.order));
    }).catch((error) => console.error('Error loading contact custom fields:', error));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.type, cfg.customFieldListId]);

  /** A row of mutually exclusive choices (same look as the button action-type picker). */
  const renderChoice = <T extends string>(options: Array<{ value: T; label: string }>, value: T, onPick: (value: T) => void) => (
    <View style={styles.actionTypeRow}>
      {options.map((option) => (
        <TouchableOpacity
          key={option.value}
          accessibilityRole="radio"
          accessibilityState={{ selected: value === option.value }}
          style={[styles.actionTypeOption, value === option.value && styles.actionTypeOptionActive]}
          onPress={() => onPick(option.value)}
        >
          <Text style={[styles.actionTypeOptionText, value === option.value && styles.actionTypeOptionTextActive]}>{option.label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  /** Toggleable chips for several values (days, months). */
  const renderChips = (options: Array<{ value: number; label: string }>, selected: number[] | undefined, onChange: (next: number[]) => void) => (
    <View style={styles.chipRow}>
      {options.map((option) => {
        const on = (selected || []).includes(option.value);
        return (
          <TouchableOpacity
            key={option.value}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: on }}
            style={[styles.chip, on && styles.actionTypeOptionActive]}
            onPress={() => onChange(on ? (selected || []).filter((v) => v !== option.value) : [...(selected || []), option.value].sort((a, b) => a - b))}
          >
            <Text style={[styles.actionTypeOptionText, on && styles.actionTypeOptionTextActive]}>{option.label}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const renderNextNodePicker = (label: string, value: string | undefined, onPick: (nextNodeId: string) => void, edgeKey: string) => (
    <View style={styles.field}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity style={styles.selectButton} onPress={() => setNodePickerFor(edgeKey)}>
        <Text style={[styles.selectButtonText, !value && styles.selectButtonPlaceholder]}>
          {value ? (otherNodes.find((n) => n.id === value)?.name || 'Checker selected') : 'Select next checker'}
        </Text>
        <Ionicons name="chevron-down" size={20} color={colors.textLight} />
      </TouchableOpacity>
      {nodePickerFor === edgeKey && (
        <Modal visible transparent animationType="slide" onRequestClose={() => setNodePickerFor(null)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Select Checker</Text>
                <TouchableOpacity onPress={() => setNodePickerFor(null)}>
                  <Ionicons name="close" size={24} color={colors.textDark} />
                </TouchableOpacity>
              </View>
              <FlatList
                data={otherNodes}
                keyExtractor={(item) => item.id}
                renderItem={({ item }) => (
                  <TouchableOpacity style={styles.option} onPress={() => { onPick(item.id); setNodePickerFor(null); }}>
                    <Text style={styles.optionLabel}>{item.name || 'Untitled checker'}</Text>
                  </TouchableOpacity>
                )}
                ListEmptyComponent={<View style={styles.emptyContainer}><Text style={styles.emptyText}>Add another checker first</Text></View>}
              />
            </View>
          </View>
        </Modal>
      )}
    </View>
  );

  const renderButtonsEditor = () => {
    const buttons = cfg.buttons || [];
    // Per-type caps: up to 10 quick-reply (go_to_node) buttons — sent as a list message once
    // there are more than 3, since WhatsApp's raw 'button' interactive type maxes out at 3
    // (see flow-engine.service.ts's send_message case); up to 2 that open a website; and only
    // 1 that calls a number. Every visit_website/call button configured is now sent as its own
    // message (flow-engine loops over all of them), so these caps are just sane authoring
    // limits, not a "only one wins" constraint. A new button always starts as go_to_node (see
    // the add handler below), so that type's cap gates the add button; changing an existing
    // button's type to visit_website/call is capped separately at the point the business picks
    // that type, further down in this function.
    const goToNodeCount = buttons.filter((b) => !b.actionType || b.actionType === 'go_to_node').length;
    const visitWebsiteCount = buttons.filter((b) => b.actionType === 'visit_website').length;
    const callCount = buttons.filter((b) => b.actionType === 'call').length;
    const atGoToNodeCap = goToNodeCount >= 10;
    const showsAsList = goToNodeCount > 3;
    return (
      <View style={styles.field}>
        <View style={styles.buttonsHeader}>
          <Text style={styles.label}>Buttons (Optional)</Text>
          <TouchableOpacity
            onPress={() => updateConfig({ buttons: [...buttons, { id: genId('btn'), label: '' }] })}
            disabled={atGoToNodeCap}
          >
            <Ionicons name="add-circle-outline" size={20} color={atGoToNodeCap ? colors.textLight : colors.primary} />
          </TouchableOpacity>
        </View>
        <Text style={styles.helperText}>Up to 10 buttons that jump to a checker (sent as a list once there are more than 3), up to 2 that open a website, and 1 that calls a number.</Text>
        {showsAsList && (
          <View style={{ marginTop: spacing.sm }}>
            <Input
              label="List button text *"
              value={cfg.listButtonText || ''}
              onChangeText={(text) => updateConfig({ listButtonText: text })}
              placeholder="e.g. View Options"
              size="sm"
              maxLength={20}
              error={errors.listButtonText}
            />
            <Text style={styles.helperText}>More than 3 buttons are sent as a list — this is the text on the button that opens it.</Text>
          </View>
        )}
        {buttons.map((button, index) => {
          const actionType = button.actionType || 'go_to_node';
          const updateButton = (patch: Partial<FlowButton>) => {
            const next = [...buttons];
            next[index] = { ...button, ...patch };
            updateConfig({ buttons: next });
          };
          return (
            <View key={button.id} style={styles.buttonRow}>
              <View style={styles.buttonRowTop}>
                <View style={{ flex: 1 }}>
                  <Input
                    placeholder="Button label"
                    value={button.label}
                    onChangeText={(text) => updateButton({ label: text })}
                    size="sm"
                  />
                </View>
                <TouchableOpacity
                  onPress={() => updateConfig({ buttons: buttons.filter((b) => b.id !== button.id) })}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  style={styles.removeButtonIcon}
                >
                  <Ionicons name="close-circle" size={20} color={colors.error} />
                </TouchableOpacity>
              </View>
              <View style={styles.actionTypeRow}>
                {([
                  { value: 'go_to_node', label: 'Go to checker', atCap: actionType !== 'go_to_node' && atGoToNodeCap },
                  { value: 'visit_website', label: 'Visit website', atCap: actionType !== 'visit_website' && visitWebsiteCount >= 2 },
                  { value: 'call', label: 'Call', atCap: actionType !== 'call' && callCount >= 1 },
                ] as const).map((opt) => (
                  <TouchableOpacity
                    key={opt.value}
                    style={[styles.actionTypeOption, actionType === opt.value && styles.actionTypeOptionActive, opt.atCap && styles.actionTypeOptionDisabled]}
                    onPress={() => !opt.atCap && updateButton({ actionType: opt.value })}
                    disabled={opt.atCap}
                  >
                    <Text style={[styles.actionTypeOptionText, actionType === opt.value && styles.actionTypeOptionTextActive]}>{opt.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              {actionType === 'go_to_node' && renderNextNodePicker(
                `"${button.label || 'Button'}" leads to`,
                button.nextNodeId,
                (nextNodeId) => updateButton({ nextNodeId }),
                `button:${button.id}`
              )}
              {actionType === 'visit_website' && (
                <Input
                  placeholder="https://example.com"
                  value={button.url || ''}
                  onChangeText={(text) => updateButton({ url: text })}
                  size="sm"
                  keyboardType="url"
                  autoCapitalize="none"
                />
              )}
              {actionType === 'call' && (
                <Input
                  placeholder="+201234567890"
                  value={button.phoneNumber || ''}
                  onChangeText={(text) => updateButton({ phoneNumber: text })}
                  size="sm"
                  keyboardType="phone-pad"
                />
              )}
            </View>
          );
        })}
      </View>
    );
  };

  switch (node.type) {
    case 'start':
      return (
        <>
          <Text style={styles.helperText}>Every flow begins here. Connect Start to whichever checker should run first.</Text>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'send_message':
    case 'ask_and_wait':
      return (
        <>
          <View style={styles.field}>
            <RichTextEditor
              label="Message *"
              value={cfg.text || ''}
              onChange={(text) => updateConfig({ text })}
              placeholder="What should this node say?"
              maxLength={4096}
              variables={availableVariables}
            />
            {errors.text && <Text style={styles.errorText}>{errors.text}</Text>}
          </View>
          <NodeMediaPickerField
            mediaUrl={cfg.mediaUrl}
            mediaType={cfg.mediaType}
            onChange={(patch) => updateConfig(patch)}
          />
          {node.type === 'ask_and_wait' && (
            <View style={styles.field}>
              <Input
                label="Save Reply As *"
                value={cfg.saveAs || ''}
                onChangeText={(text) => updateConfig({ saveAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
                placeholder="e.g. email"
                size="sm"
                error={errors.saveAs}
              />
              <Text style={styles.helperText}>Reference this later as {'{{' + (cfg.saveAs || 'variableName') + '}}'} in any message or condition.</Text>
            </View>
          )}
          {renderButtonsEditor()}
          {(cfg.buttons?.length ?? 0) === 0 && renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'branch':
      return (
        <ConditionGroupsEditor
          node={node}
          otherNodes={otherNodes}
          onChange={onChange}
          errors={errors}
        />
      );

    case 'update_contact': {
      const mode = cfg.contactUpdateMode || 'profile';
      const rows = cfg.customFieldValues || [];
      const updateRow = (id: string, patch: Partial<{ columnId: string; valueTemplate: string }>) =>
        updateConfig({ customFieldValues: rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
      const isTags = (cfg.contactField || '').replace(/^profile\./, '') === 'tags';
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>What to update</Text>
            {renderChoice([{ value: 'profile', label: 'Contact field' }, { value: 'custom', label: 'Custom fields' }], mode, (value) => updateConfig({ contactUpdateMode: value }))}
          </View>
          {mode === 'profile' ? (
            <>
              <View style={styles.field}>
                <Input
                  label="Contact Field *"
                  value={cfg.contactField || ''}
                  onChangeText={(text) => updateConfig({ contactField: text })}
                  placeholder="e.g. email, tags, customFields.birthday"
                  size="sm"
                  error={errors.contactField}
                />
                <View style={styles.chipRow}>
                  {['name', 'email', 'tags', 'location.city', 'notes'].map((field) => (
                    <TouchableOpacity key={field} style={styles.chip} onPress={() => updateConfig({ contactField: field })}>
                      <Text style={styles.actionTypeOptionText}>{field}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              {isTags && (
                <View style={styles.field}>
                  <Text style={styles.label}>Tags</Text>
                  {renderChoice([{ value: 'add', label: 'Add' }, { value: 'remove', label: 'Remove' }, { value: 'replace', label: 'Replace all' }], cfg.tagsMode || 'replace', (value) => updateConfig({ tagsMode: value }))}
                  <Text style={styles.helperText}>Separate several tags with commas.</Text>
                </View>
              )}
              <View style={styles.field}>
                <InterpolatableInput
                  label="Value *"
                  value={cfg.valueTemplate || ''}
                  onChangeText={(text) => updateConfig({ valueTemplate: text })}
                  placeholder="e.g. {{email}}"
                  size="sm"
                  error={errors.valueTemplate}
                  variables={availableVariables}
                />
                <Text style={styles.helperText}>Use {'{{variableName}}'} to insert a value captured earlier in the flow, or tap the icon to pick one.</Text>
              </View>
              {(cfg.profileUpdates || []).map((u) => {
                const extras = cfg.profileUpdates || [];
                const setExtra = (patch: Partial<typeof u>) => updateConfig({ profileUpdates: extras.map((x) => (x.id === u.id ? { ...x, ...patch } : x)) });
                return (
                  <View key={u.id} style={styles.buttonRow}>
                    <View style={styles.buttonRowTop}>
                      <View style={{ flex: 1 }}>
                        <Input value={u.contactField} onChangeText={(text) => setExtra({ contactField: text })} placeholder="Another field, e.g. location.city" size="sm" />
                      </View>
                      <TouchableOpacity accessibilityLabel="Remove field" onPress={() => updateConfig({ profileUpdates: extras.filter((x) => x.id !== u.id) })} style={styles.removeButtonIcon}>
                        <Ionicons name="close-circle" size={20} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                    {u.contactField.replace(/^profile\./, '') === 'tags' && renderChoice([{ value: 'add', label: 'Add' }, { value: 'remove', label: 'Remove' }, { value: 'replace', label: 'Replace all' }],
                      u.tagsMode || 'replace', (value) => setExtra({ tagsMode: value }))}
                    <View style={{ marginTop: spacing.xs }}>
                      <InterpolatableInput label="Value" value={u.valueTemplate || ''} onChangeText={(text) => setExtra({ valueTemplate: text })} size="sm" variables={availableVariables} />
                    </View>
                  </View>
                );
              })}
              <TouchableOpacity style={styles.addButtonRow} onPress={() => updateConfig({ profileUpdates: [...(cfg.profileUpdates || []), { id: genId('pf'), contactField: '', valueTemplate: '' }] })}>
                <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
                <Text style={styles.addButtonRowText}>Update another field</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <View style={styles.field}>
                <Text style={styles.label}>Contacts data list *</Text>
                <TouchableOpacity style={styles.selectButton} onPress={() => setContactListPickerOpen(true)}>
                  <Text style={[styles.selectButtonText, !cfg.customFieldListId && styles.selectButtonPlaceholder]}>
                    {cfg.customFieldListId ? contactLists.find((l) => l._id === cfg.customFieldListId)?.name || 'List selected' : 'Select a list'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={colors.textLight} />
                </TouchableOpacity>
                <Text style={styles.helperText}>The custom fields you added to a Contacts data list — saved exactly like the contact screen's Extra Fields.</Text>
                {errors.customFieldListId && <Text style={styles.errorText}>{errors.customFieldListId}</Text>}
              </View>
              {!!cfg.customFieldListId && (
                <View style={styles.field}>
                  <View style={styles.buttonsHeader}>
                    <Text style={styles.label}>Fields</Text>
                    <TouchableOpacity accessibilityLabel="Add field" onPress={() => updateConfig({ customFieldValues: [...rows, { id: genId('cf'), columnId: '', valueTemplate: '' }] })}>
                      <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                    </TouchableOpacity>
                  </View>
                  {errors.customFieldValues && <Text style={styles.errorText}>{errors.customFieldValues}</Text>}
                  {rows.map((row) => {
                    const column = contactListColumns.find((c) => c.id === row.columnId);
                    return (
                      <View key={row.id} style={styles.buttonRow}>
                        <View style={styles.buttonRowTop}>
                          <TouchableOpacity style={[styles.selectButton, { flex: 1 }]} onPress={() => setCustomColumnPickerFor(row.id)}>
                            <Text style={[styles.selectButtonText, !column && styles.selectButtonPlaceholder]} numberOfLines={1}>{column ? `${column.name} (${column.type})` : 'Select a field'}</Text>
                            <Ionicons name="chevron-down" size={18} color={colors.textLight} />
                          </TouchableOpacity>
                          <TouchableOpacity accessibilityLabel="Remove field" onPress={() => updateConfig({ customFieldValues: rows.filter((r) => r.id !== row.id) })} style={styles.removeButtonIcon}>
                            <Ionicons name="close-circle" size={20} color={colors.error} />
                          </TouchableOpacity>
                        </View>
                        <View style={{ marginTop: spacing.xs }}>
                          <InterpolatableInput
                            label="Value"
                            value={row.valueTemplate}
                            onChangeText={(text) => updateRow(row.id, { valueTemplate: text })}
                            placeholder={column?.type === 'boolean' ? 'true / false' : column?.type === 'date' ? 'YYYY-MM-DD or {{variable}}' : 'e.g. {{answer}}'}
                            size="sm"
                            variables={availableVariables}
                          />
                        </View>
                      </View>
                    );
                  })}
                  <Text style={styles.helperText}>An empty value leaves that field unchanged. Values are checked against the field type (number, date, yes/no…).</Text>
                </View>
              )}
            </>
          )}
          {/* Rendered unconditionally: renderSimpleListModal uses a hook. */}
          {renderSimpleListModal(contactListPickerOpen, () => setContactListPickerOpen(false), contactLists.map((l) => ({ id: l._id, label: l.name })),
            (id) => updateConfig({ customFieldListId: id, customFieldValues: id === cfg.customFieldListId ? rows : [{ id: genId('cf'), columnId: '', valueTemplate: '' }] }),
            loadingOptions, 'Select a list', 'No Contacts data list with custom fields yet')}
          {renderSimpleListModal(!!customColumnPickerFor, () => setCustomColumnPickerFor(null),
            contactListColumns.filter((c) => !['relation', 'repeater'].includes(c.type)).map((c) => ({ id: c.id, label: `${c.name} (${c.type})` })),
            (id) => { if (customColumnPickerFor) updateRow(customColumnPickerFor, { columnId: id }); }, false, 'Select a field', 'This list has no custom fields')}
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'assign_user':
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>Assign To *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setUserPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !(cfg.userIds?.length) && styles.selectButtonPlaceholder]}>
                {cfg.userIds?.length ? `${cfg.userIds.length} user${cfg.userIds.length === 1 ? '' : 's'} selected` : 'Select users'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.userIds && <Text style={styles.errorText}>{errors.userIds}</Text>}
          </View>
          <BusinessUserPicker
            visible={userPickerOpen}
            onClose={() => setUserPickerOpen(false)}
            onConfirm={(ids) => { updateConfig({ userIds: ids }); setUserPickerOpen(false); }}
            mode="multiple"
            source="users"
            selectedUserIds={cfg.userIds || []}
          />
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'assign_department':
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>Assign To *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setDepartmentPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !(cfg.departmentIds?.length) && styles.selectButtonPlaceholder]}>
                {cfg.departmentIds?.length ? `${cfg.departmentIds.length} department${cfg.departmentIds.length === 1 ? '' : 's'} selected` : 'Select departments'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.departmentIds && <Text style={styles.errorText}>{errors.departmentIds}</Text>}
          </View>
          <BusinessUserPicker
            visible={departmentPickerOpen}
            onClose={() => setDepartmentPickerOpen(false)}
            onConfirm={(ids) => { updateConfig({ departmentIds: ids }); setDepartmentPickerOpen(false); }}
            mode="multiple"
            source="departments"
            selectedUserIds={cfg.departmentIds || []}
          />
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'send_dataset':
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>Dataset *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setDatasetPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !cfg.setId && styles.selectButtonPlaceholder]}>
                {cfg.setId ? datasets.find((d) => d._id === cfg.setId)?.name || 'Dataset selected' : 'Select dataset'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.setId && <Text style={styles.errorText}>{errors.setId}</Text>}
          </View>
          {renderSimpleListModal(datasetPickerOpen, () => setDatasetPickerOpen(false), datasets.map((d) => ({ id: d._id, label: d.name })), (id) => updateConfig({ setId: id }), loadingOptions)}
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'write_post':
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.helperText}>
              Publishes a new post on the Facebook Page this flow is attached to. Only Facebook is supported today.
            </Text>
          </View>
          <View style={styles.field}>
            <RichTextEditor
              label="Post text *"
              value={cfg.text || ''}
              onChange={(text) => updateConfig({ text })}
              placeholder="What should this post say?"
              maxLength={4096}
              variables={availableVariables}
            />
            {errors.text && <Text style={styles.errorText}>{errors.text}</Text>}
          </View>
          <NodeMediaPickerField
            mediaUrl={cfg.mediaUrl}
            mediaType={cfg.mediaType}
            onChange={(patch) => updateConfig(patch)}
          />
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'show_product':
      return (
        <>
          {renderShowSource('product')}
          {renderSimpleListModal(productPickerOpen, () => setProductPickerOpen(false), products.map((p) => ({ id: p._id, label: p.name })), (id) => updateConfig({ productId: id }), loadingOptions)}
          <View style={styles.field}>
            <InterpolatableInput
              label="Message (Optional)"
              value={cfg.text || ''}
              onChangeText={(text) => updateConfig({ text })}
              placeholder="Here is the product you asked about"
              size="sm"
              multiline
              variables={availableVariables}
            />
            <Text style={styles.helperText}>Sent with the product photo, name, price and a button to open it and order.</Text>
          </View>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'show_catalog':
      return (
        <>
          {renderShowSource('catalog')}
          {renderSimpleListModal(catalogPickerOpen, () => setCatalogPickerOpen(false), catalogs.map((c) => ({ id: c._id, label: c.name })), (id) => updateConfig({ catalogId: id }), loadingOptions)}
          <View style={styles.field}>
            <InterpolatableInput
              label="Message (Optional)"
              value={cfg.text || ''}
              onChangeText={(text) => updateConfig({ text })}
              placeholder="Browse our catalogue"
              size="sm"
              multiline
              variables={availableVariables}
            />
            <Text style={styles.helperText}>Sent with a button that opens the catalogue.</Text>
          </View>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'open_url':
      return (
        <>
          <View style={styles.field}>
            <Input
              label="URL *"
              value={cfg.url || ''}
              onChangeText={(text) => updateConfig({ url: text })}
              placeholder="https://..."
              size="sm"
              error={errors.url}
              autoCapitalize="none"
            />
          </View>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'webhook':
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>Webhook *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setWebhookPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !cfg.webhookId && styles.selectButtonPlaceholder]}>
                {cfg.webhookId ? webhooks.find((w) => w._id === cfg.webhookId)?.title || 'Webhook selected' : 'Select webhook'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.webhookId && <Text style={styles.errorText}>{errors.webhookId}</Text>}
            <Text style={styles.helperText}>Only fires when this flow is attached to a WhatsApp number.</Text>
          </View>
          {renderSimpleListModal(webhookPickerOpen, () => setWebhookPickerOpen(false), webhooks.map((w) => ({ id: w._id, label: w.title })), (id) => updateConfig({ webhookId: id }), loadingOptions)}
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'create_task': {
      const taskPriority = cfg.taskPriority || 'normal';
      return (
        <>
          <View style={styles.field}>
            <InterpolatableInput label="Task Title *" value={cfg.taskTitle || ''} onChangeText={(text) => updateConfig({ taskTitle: text })} placeholder="Task title" size="sm" error={errors.taskTitle} variables={availableVariables} />
          </View>
          <View style={styles.field}>
            <RichTextEditor label="Description (Optional)" value={cfg.taskDescription || ''} onChange={(text) => updateConfig({ taskDescription: text })} placeholder="Task details" maxLength={1000} showToolbar={false} variables={availableVariables} />
          </View>
          <View style={styles.field}>
            <Text style={styles.label}>Priority</Text>
            <View style={styles.actionTypeRow}>
              {(['low', 'normal', 'high', 'urgent'] as const).map((priority) => (
                <TouchableOpacity
                  key={priority}
                  style={[styles.actionTypeOption, taskPriority === priority && styles.actionTypeOptionActive]}
                  onPress={() => updateConfig({ taskPriority: priority })}
                >
                  <Text style={[styles.actionTypeOptionText, taskPriority === priority && styles.actionTypeOptionTextActive]}>{priority.charAt(0).toUpperCase() + priority.slice(1)}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <View style={styles.field}>
            <InterpolatableInput
              label="Due Date (Optional, ISO 8601 or {{variable}})"
              value={cfg.taskDueDate || ''}
              onChangeText={(text) => updateConfig({ taskDueDate: text })}
              placeholder="2026-07-20T09:00:00Z"
              size="sm"
              autoCapitalize="none"
              variables={availableVariables}
            />
          </View>
          <View style={styles.field}>
            <Text style={styles.label}>Assign To *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setUserPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !(cfg.userIds?.length) && styles.selectButtonPlaceholder]}>
                {cfg.userIds?.length ? `${cfg.userIds.length} user${cfg.userIds.length === 1 ? '' : 's'} selected` : 'Select users'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.userIds && <Text style={styles.errorText}>{errors.userIds}</Text>}
          </View>
          <BusinessUserPicker
            visible={userPickerOpen}
            onClose={() => setUserPickerOpen(false)}
            onConfirm={(ids) => { updateConfig({ userIds: ids }); setUserPickerOpen(false); }}
            mode="multiple"
            source="users"
            selectedUserIds={cfg.userIds || []}
          />
          <View style={styles.field}>
            <Input
              label="Save Result As (Optional)"
              value={cfg.saveResultAs || ''}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder="e.g. createdTask"
              size="sm"
            />
            <Text style={styles.helperText}>Reference later as {'{{' + (cfg.saveResultAs || 'createdTask') + '.taskId}}'}.</Text>
          </View>
          <Text style={styles.helperText}>Only fires when this flow is attached to a WhatsApp number.</Text>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'create_calendar_event': {
      // Resolve the effective mode: explicit cfg.bookingMode when set, falling back to the
      // original implicit convention (both dates set = fixed_time, unset = slots) for nodes
      // saved before bookingMode existed.
      const bookingMode: 'fixed_time' | 'slots' | 'browser' =
        cfg.bookingMode || (cfg.eventStartDate && cfg.eventEndDate ? 'fixed_time' : 'slots');
      const isPausingMode = bookingMode !== 'fixed_time';
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>Calendar *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setCalendarPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !cfg.calendarId && styles.selectButtonPlaceholder]}>
                {cfg.calendarId ? calendars.find((c) => c._id === cfg.calendarId)?.name || 'Calendar selected' : 'Select calendar'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.calendarId && <Text style={styles.errorText}>{errors.calendarId}</Text>}
          </View>
          {renderSimpleListModal(calendarPickerOpen, () => setCalendarPickerOpen(false), calendars.map((c) => ({ id: c._id, label: c.name })), (id) => updateConfig({ calendarId: id }), loadingOptions)}
          <View style={styles.field}>
            <InterpolatableInput label="Event Title *" value={cfg.eventTitle || ''} onChangeText={(text) => updateConfig({ eventTitle: text })} placeholder="Event title" size="sm" error={errors.eventTitle} variables={availableVariables} />
          </View>
          <View style={styles.field}>
            <Text style={styles.label}>Booking Mode</Text>
            <View style={styles.actionTypeRow}>
              <TouchableOpacity
                style={[styles.actionTypeOption, bookingMode === 'slots' && styles.actionTypeOptionActive]}
                onPress={() => updateConfig({ bookingMode: 'slots', eventStartDate: undefined, eventEndDate: undefined })}
              >
                <Text style={[styles.actionTypeOptionText, bookingMode === 'slots' && styles.actionTypeOptionTextActive]}>Offer available slots</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionTypeOption, bookingMode === 'fixed_time' && styles.actionTypeOptionActive]}
                onPress={() => updateConfig({ bookingMode: 'fixed_time', eventStartDate: cfg.eventStartDate || '', eventEndDate: cfg.eventEndDate || '' })}
              >
                <Text style={[styles.actionTypeOptionText, bookingMode === 'fixed_time' && styles.actionTypeOptionTextActive]}>Fixed time</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionTypeOption, bookingMode === 'browser' && styles.actionTypeOptionActive]}
                onPress={() => updateConfig({ bookingMode: 'browser', eventStartDate: undefined, eventEndDate: undefined })}
              >
                <Text style={[styles.actionTypeOptionText, bookingMode === 'browser' && styles.actionTypeOptionTextActive]}>Open booking page</Text>
              </TouchableOpacity>
            </View>
          </View>
          {bookingMode === 'slots' && (
            <View style={styles.field}>
              <Input
                label="Offer Slots For (days ahead)"
                value={cfg.slotWindowDays ? String(cfg.slotWindowDays) : ''}
                onChangeText={(text) => updateConfig({ slotWindowDays: text ? Number(text.replace(/[^0-9]/g, '')) : undefined })}
                placeholder="7"
                size="sm"
                keyboardType="numeric"
              />
              <Text style={styles.helperText}>Available times are computed live from the selected calendar's availability and booking limits, and sent as a tap-to-book list (up to 10 soonest, across up to 7 days).</Text>
            </View>
          )}
          {bookingMode === 'fixed_time' && (
            <>
              <View style={styles.field}>
                <InterpolatableInput
                  label="Start Date/Time (ISO 8601 or {{variable}})"
                  value={cfg.eventStartDate || ''}
                  onChangeText={(text) => updateConfig({ eventStartDate: text })}
                  placeholder="2026-07-15T14:00:00Z"
                  size="sm"
                  autoCapitalize="none"
                  error={errors.eventStartDate}
                  variables={availableVariables}
                />
              </View>
              <View style={styles.field}>
                <InterpolatableInput
                  label="End Date/Time (ISO 8601 or {{variable}})"
                  value={cfg.eventEndDate || ''}
                  onChangeText={(text) => updateConfig({ eventEndDate: text })}
                  placeholder="2026-07-15T15:00:00Z"
                  size="sm"
                  autoCapitalize="none"
                  error={errors.eventEndDate}
                  variables={availableVariables}
                />
              </View>
            </>
          )}
          {bookingMode === 'browser' && (
            <View style={styles.field}>
              <Text style={styles.helperText}>Sends a link to a booking page where the contact can pick ANY future date using a full calendar — best when the next few slots offered in-chat might not fit, or bookings need to reach further out than a week. The flow pauses and continues once the contact finishes booking on that page.</Text>
            </View>
          )}
          {isPausingMode && (
            <View style={styles.field}>
              <InterpolatableInput
                label="Prompt Message"
                value={cfg.text || ''}
                onChangeText={(text) => updateConfig({ text })}
                placeholder={bookingMode === 'slots' ? 'Please pick a time:' : 'Please pick a time that works for you:'}
                size="sm"
                multiline
                variables={availableVariables}
              />
              <Text style={styles.helperText}>{bookingMode === 'slots' ? 'Shown above the list of available time slots.' : 'Shown alongside the button that opens the booking page.'}</Text>
            </View>
          )}
          <View style={styles.field}>
            <InterpolatableInput
              label="Confirmation Message (Optional)"
              value={cfg.confirmationMessage || ''}
              onChangeText={(text) => updateConfig({ confirmationMessage: text })}
              placeholder="Your appointment has been confirmed!"
              size="sm"
              multiline
              variables={availableVariables}
            />
            <Text style={styles.helperText}>Sent after a successful booking, along with a downloadable calendar (.ics) file. Supports {'{{' + (cfg.saveResultAs || 'bookedEvent') + '.title}}'} etc.</Text>
          </View>
          <View style={styles.field}>
            <Input
              label="Save Result As (Optional)"
              value={cfg.saveResultAs || ''}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder="e.g. bookedEvent"
              size="sm"
            />
            <Text style={styles.helperText}>Reference later as {'{{' + (cfg.saveResultAs || 'bookedEvent') + '.title}}'}, {'{{' + (cfg.saveResultAs || 'bookedEvent') + '.startDate}}'}, etc.</Text>
          </View>
          <Text style={styles.helperText}>Only fires when this flow is attached to a WhatsApp number.</Text>
          {renderNextNodePicker(isPausingMode ? 'After booking completes' : 'Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'find_contact_tasks':
      return (
        <>
          <Text style={styles.helperText}>Looks up this contact's open tasks (no configuration needed — always the contact's current open/active tasks).</Text>
          <View style={styles.field}>
            <Input
              label="Save Result As (Optional)"
              value={cfg.saveResultAs || ''}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder="e.g. contactTasks"
              size="sm"
            />
            <Text style={styles.helperText}>Reference later as {'{{' + (cfg.saveResultAs || 'contactTasks') + '.count}}'} — e.g. "You have {'{{' + (cfg.saveResultAs || 'contactTasks') + '.count}}'} open tasks."</Text>
          </View>
          <Text style={styles.helperText}>Only fires when this flow is attached to a WhatsApp number.</Text>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'find_contact_events':
      return (
        <>
          <Text style={styles.helperText}>Looks up this contact's upcoming calendar events across all your calendars (no configuration needed).</Text>
          <View style={styles.field}>
            <Input
              label="Save Result As (Optional)"
              value={cfg.saveResultAs || ''}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder="e.g. upcomingEvents"
              size="sm"
            />
            <Text style={styles.helperText}>Reference later as {'{{' + (cfg.saveResultAs || 'upcomingEvents') + '.count}}'}, {'{{' + (cfg.saveResultAs || 'upcomingEvents') + '.nextEvent.startDate}}'}, etc.</Text>
          </View>
          <Text style={styles.helperText}>Only fires when this flow is attached to a WhatsApp number.</Text>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'set_context_field': {
      const contextFields = cfg.contextFields || [];
      const updateField = (index: number, patch: Partial<NonNullable<FlowNodeConfig['contextFields']>[number]>) => {
        const next = [...contextFields];
        next[index] = { ...next[index], ...patch };
        updateConfig({ contextFields: next });
      };
      return (
        <>
          {availableVariables.some((g) => g.entries.length > 0) && (
            <View style={styles.field}>
              <Text style={styles.label}>Available Variables</Text>
              <Text style={styles.helperText}>Values already captured earlier in this flow. Add a field below with a matching name to override one.</Text>
              <View style={styles.referenceBox}>
                {availableVariables.map((group) => group.entries.length > 0 && (
                  <View key={group.key}>
                    <Text style={styles.referenceGroupLabel}>{group.label}</Text>
                    {group.entries.map((entry) => (
                      <Text key={entry.path} style={styles.referenceEntry}>{'{{' + entry.path + '}}'} — {entry.label}</Text>
                    ))}
                  </View>
                ))}
              </View>
            </View>
          )}

          <View style={styles.field}>
            <View style={styles.buttonsHeader}>
              <Text style={styles.label}>Fields</Text>
              <View style={{ flexDirection: 'row', gap: spacing.md }}>
                <TouchableOpacity onPress={() => setContextFieldPickerOpen(true)}>
                  <Ionicons name="link-outline" size={20} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => updateConfig({ contextFields: [...contextFields, { id: genId('ctx'), fieldName: '', dataType: 'string', valueTemplate: '' }] })}>
                  <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                </TouchableOpacity>
              </View>
            </View>
            <Text style={styles.helperText}>Tap the link icon to override an existing variable, or add to set a brand-new one.</Text>
            <VariablePicker
              visible={contextFieldPickerOpen}
              onClose={() => setContextFieldPickerOpen(false)}
              groups={availableVariables}
              onSelect={(path) => updateConfig({ contextFields: [...contextFields, { id: genId('ctx'), fieldName: path, dataType: 'string', valueTemplate: '' }] })}
            />

            {contextFields.map((field, index) => (
              <View key={field.id} style={styles.buttonRow}>
                <View style={styles.buttonRowTop}>
                  <View style={{ flex: 1 }}>
                    <Input
                      placeholder="Field name, e.g. email"
                      value={field.fieldName}
                      onChangeText={(text) => updateField(index, { fieldName: text })}
                      size="sm"
                      autoCapitalize="none"
                    />
                  </View>
                  <TouchableOpacity
                    onPress={() => updateConfig({ contextFields: contextFields.filter((f) => f.id !== field.id) })}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    style={styles.removeButtonIcon}
                  >
                    <Ionicons name="close-circle" size={20} color={colors.error} />
                  </TouchableOpacity>
                </View>
                <View style={styles.actionTypeRow}>
                  {(['string', 'number', 'boolean', 'date'] as const).map((dt) => (
                    <TouchableOpacity
                      key={dt}
                      style={[styles.actionTypeOption, field.dataType === dt && styles.actionTypeOptionActive]}
                      onPress={() => updateField(index, { dataType: dt })}
                    >
                      <Text style={[styles.actionTypeOptionText, field.dataType === dt && styles.actionTypeOptionTextActive]}>{dt.charAt(0).toUpperCase() + dt.slice(1)}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {field.dataType === 'boolean' ? (
                  <View style={styles.actionTypeRow}>
                    {(['true', 'false'] as const).map((v) => (
                      <TouchableOpacity
                        key={v}
                        style={[styles.actionTypeOption, field.valueTemplate === v && styles.actionTypeOptionActive]}
                        onPress={() => updateField(index, { valueTemplate: v })}
                      >
                        <Text style={[styles.actionTypeOptionText, field.valueTemplate === v && styles.actionTypeOptionTextActive]}>{v === 'true' ? 'Yes' : 'No'}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                ) : field.dataType === 'number' ? (
                  <Input
                    placeholder="e.g. 42 or {{variable}}"
                    value={field.valueTemplate}
                    onChangeText={(text) => updateField(index, { valueTemplate: text })}
                    size="sm"
                    keyboardType="numeric"
                  />
                ) : (
                  <InterpolatableInput
                    placeholder={field.dataType === 'date' ? '2026-07-20T09:00:00Z or {{variable}}' : 'Value'}
                    value={field.valueTemplate}
                    onChangeText={(text) => updateField(index, { valueTemplate: text })}
                    size="sm"
                    autoCapitalize={field.dataType === 'date' ? 'none' : 'sentences'}
                    variables={availableVariables}
                  />
                )}
              </View>
            ))}
          </View>

          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'call_flow': {
      const callFlowInputs = cfg.callFlowInputs || [];
      const updateInput = (index: number, patch: Partial<NonNullable<FlowNodeConfig['callFlowInputs']>[number]>) => {
        const next = [...callFlowInputs];
        next[index] = { ...next[index], ...patch };
        updateConfig({ callFlowInputs: next });
      };
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>Flow to Call *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setCallFlowPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !cfg.callFlowId && styles.selectButtonPlaceholder]}>
                {cfg.callFlowId ? callableFlows.find((f) => f._id === cfg.callFlowId)?.name || 'Flow selected' : 'Select a flow'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.callFlowId && <Text style={styles.errorText}>{errors.callFlowId}</Text>}
            <Text style={styles.helperText}>This flow pauses here — including across multiple contact replies — until the called flow finishes, then continues below. The called flow must have at least one published "End" it can actually reach.</Text>
          </View>
          {renderSimpleListModal(callFlowPickerOpen, () => setCallFlowPickerOpen(false), callableFlows.map((f) => ({ id: f._id, label: f.name })), (id) => updateConfig({ callFlowId: id }), loadingOptions)}

          <View style={styles.field}>
            <View style={styles.buttonsHeader}>
              <Text style={styles.label}>Inputs (Optional)</Text>
              <TouchableOpacity onPress={() => updateConfig({ callFlowInputs: [...callFlowInputs, { id: genId('cfi'), paramName: '', valueTemplate: '' }] })}>
                <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
            </View>
            <Text style={styles.helperText}>Values passed into the called flow's context at start — reference the value you type here from THIS flow's own variables.</Text>
            {callFlowInputs.map((input, index) => (
              <View key={input.id} style={styles.buttonRow}>
                <View style={styles.buttonRowTop}>
                  <View style={{ flex: 1 }}>
                    <Input
                      placeholder="Input name, e.g. orderId"
                      value={input.paramName}
                      onChangeText={(text) => updateInput(index, { paramName: text.replace(/[^a-zA-Z0-9_]/g, '') })}
                      size="sm"
                      autoCapitalize="none"
                    />
                  </View>
                  <TouchableOpacity
                    onPress={() => updateConfig({ callFlowInputs: callFlowInputs.filter((i) => i.id !== input.id) })}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    style={styles.removeButtonIcon}
                  >
                    <Ionicons name="close-circle" size={20} color={colors.error} />
                  </TouchableOpacity>
                </View>
                <InterpolatableInput
                  placeholder="Value, e.g. {{someVariable}}"
                  value={input.valueTemplate}
                  onChangeText={(text) => updateInput(index, { valueTemplate: text })}
                  size="sm"
                  variables={availableVariables}
                />
              </View>
            ))}
          </View>

          <View style={styles.field}>
            <Input
              label="Save Result As (Optional)"
              value={cfg.saveResultAs || ''}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder="e.g. callResult"
              size="sm"
            />
            <Text style={styles.helperText}>Captures everything the called flow set in its own context. Reference later as {'{{' + (cfg.saveResultAs || 'callResult') + '.someValue}}'}, using whatever names the called flow itself uses.</Text>
          </View>

          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'write_data_list': {
      const operation = cfg.dataListOperation || 'create';
      const valueMappings = cfg.dataListValueMappings || [];
      const matchCriteria = cfg.dataListMatchCriteria || [];
      const showMappings = operation === 'create' || operation === 'update';
      const showCriteria = operation === 'update' || operation === 'find' || operation === 'delete';

      const columnLabel = (columnId: string) =>
        selectedListColumns.find((c) => c.id === columnId)?.name || t('dataLists.flowNode.selectColumn');

      const updateMapping = (index: number, patch: Partial<NonNullable<FlowNodeConfig['dataListValueMappings']>[number]>) => {
        const next = [...valueMappings];
        next[index] = { ...next[index], ...patch };
        updateConfig({ dataListValueMappings: next });
      };
      const updateCriterion = (index: number, patch: Partial<NonNullable<FlowNodeConfig['dataListMatchCriteria']>[number]>) => {
        const next = [...matchCriteria];
        next[index] = { ...next[index], ...patch };
        updateConfig({ dataListMatchCriteria: next });
      };

      const operatorOptions = [
        { value: 'eq', label: t('dataLists.flowNode.operator.eq') },
        { value: 'contains', label: t('dataLists.flowNode.operator.contains') },
        { value: 'gt', label: t('dataLists.flowNode.operator.gt') },
        { value: 'lt', label: t('dataLists.flowNode.operator.lt') },
        { value: 'gte', label: t('dataLists.flowNode.operator.gte') },
        { value: 'lte', label: t('dataLists.flowNode.operator.lte') },
      ] as const;

      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>{t('dataLists.flowNode.listLabel')}</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setDataListPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !cfg.dataListId && styles.selectButtonPlaceholder]}>
                {cfg.dataListId ? (dataLists.find((l) => l._id === cfg.dataListId)?.name || t('dataLists.flowNode.listSelected')) : t('dataLists.flowNode.selectList')}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.dataListId && <Text style={styles.errorText}>{errors.dataListId}</Text>}
          </View>
          {renderSimpleListModal(
            dataListPickerOpen,
            () => setDataListPickerOpen(false),
            dataLists.map((l) => ({ id: l._id, label: l.name })),
            (id) => updateConfig({ dataListId: id, dataListValueMappings: [], dataListMatchCriteria: [] }),
            loadingOptions,
            t('dataLists.flowNode.pickerTitle'),
            t('dataLists.flowNode.pickerEmpty'),
          )}

          <View style={styles.field}>
            <Text style={styles.label}>{t('dataLists.flowNode.operationLabel')}</Text>
            <View style={styles.actionTypeRow}>
              {([
                { value: 'create', label: t('dataLists.flowNode.operation.create') },
                { value: 'update', label: t('dataLists.flowNode.operation.update') },
                { value: 'find', label: t('dataLists.flowNode.operation.find') },
                { value: 'delete', label: t('dataLists.flowNode.operation.delete') },
              ] as const).map((opt) => (
                <TouchableOpacity
                  key={opt.value}
                  style={[styles.actionTypeOption, operation === opt.value && styles.actionTypeOptionActive]}
                  onPress={() => updateConfig({ dataListOperation: opt.value })}
                >
                  <Text style={[styles.actionTypeOptionText, operation === opt.value && styles.actionTypeOptionTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            {errors.dataListOperation && <Text style={styles.errorText}>{errors.dataListOperation}</Text>}
          </View>

          {showMappings && (
            <View style={styles.field}>
              <View style={styles.buttonsHeader}>
                <Text style={styles.label}>{t('dataLists.flowNode.valueMappings')}</Text>
                <TouchableOpacity
                  disabled={!cfg.dataListId}
                  onPress={() => updateConfig({ dataListValueMappings: [...valueMappings, { id: genId('dlv'), columnId: '', valueTemplate: '' }] })}
                >
                  <Ionicons name="add-circle-outline" size={20} color={cfg.dataListId ? colors.primary : colors.textLight} />
                </TouchableOpacity>
              </View>
              <Text style={styles.helperText}>{t('dataLists.flowNode.valueMappingsHint')}</Text>
              {errors.dataListValueMappings && <Text style={styles.errorText}>{errors.dataListValueMappings}</Text>}
              {valueMappings.map((mapping, index) => (
                <View key={mapping.id} style={styles.buttonRow}>
                  <View style={styles.buttonRowTop}>
                    <View style={{ flex: 1 }}>
                      <TouchableOpacity style={styles.selectButton} onPress={() => setNodePickerFor(`dlv:${mapping.id}`)}>
                        <Text style={[styles.selectButtonText, !mapping.columnId && styles.selectButtonPlaceholder]}>{columnLabel(mapping.columnId)}</Text>
                        <Ionicons name="chevron-down" size={18} color={colors.textLight} />
                      </TouchableOpacity>
                    </View>
                    <TouchableOpacity
                      onPress={() => updateConfig({ dataListValueMappings: valueMappings.filter((m) => m.id !== mapping.id) })}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      style={styles.removeButtonIcon}
                    >
                      <Ionicons name="close-circle" size={20} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  <InterpolatableInput
                    placeholder={t('dataLists.flowNode.valuePlaceholder')}
                    value={mapping.valueTemplate}
                    onChangeText={(text) => updateMapping(index, { valueTemplate: text })}
                    size="sm"
                    variables={availableVariables}
                  />
                  {renderSimpleListModal(
                    nodePickerFor === `dlv:${mapping.id}`,
                    () => setNodePickerFor(null),
                    selectedListColumns.map((c) => ({ id: c.id, label: c.name })),
                    (id) => updateMapping(index, { columnId: id }),
                    false,
                    t('dataLists.flowNode.selectColumn'),
                  )}
                </View>
              ))}
            </View>
          )}

          {showCriteria && (
            <View style={styles.field}>
              <View style={styles.buttonsHeader}>
                <Text style={styles.label}>{t('dataLists.flowNode.matchCriteria')}</Text>
                <TouchableOpacity
                  disabled={!cfg.dataListId}
                  onPress={() => updateConfig({ dataListMatchCriteria: [...matchCriteria, { id: genId('dlm'), columnId: '', operator: 'eq', valueTemplate: '' }] })}
                >
                  <Ionicons name="add-circle-outline" size={20} color={cfg.dataListId ? colors.primary : colors.textLight} />
                </TouchableOpacity>
              </View>
              <Text style={styles.helperText}>{t('dataLists.flowNode.matchCriteriaHint')}</Text>
              {errors.dataListMatchCriteria && <Text style={styles.errorText}>{errors.dataListMatchCriteria}</Text>}
              {matchCriteria.map((criterion, index) => (
                <View key={criterion.id} style={styles.buttonRow}>
                  <View style={styles.buttonRowTop}>
                    <View style={{ flex: 1 }}>
                      <TouchableOpacity style={styles.selectButton} onPress={() => setNodePickerFor(`dlm-col:${criterion.id}`)}>
                        <Text style={[styles.selectButtonText, !criterion.columnId && styles.selectButtonPlaceholder]}>{columnLabel(criterion.columnId)}</Text>
                        <Ionicons name="chevron-down" size={18} color={colors.textLight} />
                      </TouchableOpacity>
                    </View>
                    <TouchableOpacity
                      onPress={() => updateConfig({ dataListMatchCriteria: matchCriteria.filter((c) => c.id !== criterion.id) })}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      style={styles.removeButtonIcon}
                    >
                      <Ionicons name="close-circle" size={20} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  <TouchableOpacity style={[styles.selectButton, { marginTop: spacing.xs }]} onPress={() => setNodePickerFor(`dlm-op:${criterion.id}`)}>
                    <Text style={styles.selectButtonText}>{operatorOptions.find((o) => o.value === criterion.operator)?.label}</Text>
                    <Ionicons name="chevron-down" size={18} color={colors.textLight} />
                  </TouchableOpacity>
                  <InterpolatableInput
                    placeholder={t('dataLists.flowNode.valuePlaceholder')}
                    value={criterion.valueTemplate}
                    onChangeText={(text) => updateCriterion(index, { valueTemplate: text })}
                    size="sm"
                    variables={availableVariables}
                  />
                  {renderSimpleListModal(
                    nodePickerFor === `dlm-col:${criterion.id}`,
                    () => setNodePickerFor(null),
                    selectedListColumns.map((c) => ({ id: c.id, label: c.name })),
                    (id) => updateCriterion(index, { columnId: id }),
                    false,
                    t('dataLists.flowNode.selectColumn'),
                  )}
                  {renderSimpleListModal(
                    nodePickerFor === `dlm-op:${criterion.id}`,
                    () => setNodePickerFor(null),
                    operatorOptions.map((o) => ({ id: o.value, label: o.label })),
                    (id) => updateCriterion(index, { operator: id as NonNullable<FlowNodeConfig['dataListMatchCriteria']>[number]['operator'] }),
                    false,
                    t('dataLists.flowNode.operationLabel'),
                  )}
                </View>
              ))}
            </View>
          )}

          {operation === 'find' && (
            <View style={styles.field}>
              <Input
                label={t('dataLists.flowNode.saveResultAs')}
                value={cfg.saveResultAs || ''}
                onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
                placeholder={t('dataLists.flowNode.saveResultAsPlaceholder')}
                size="sm"
              />
              <Text style={styles.helperText}>{t('dataLists.flowNode.saveResultAsHint')}</Text>
            </View>
          )}

          {renderNextNodePicker(t('dataLists.flowNode.then'), cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'check_language': {
      const routes = cfg.languageRoutes || [];

      const updateRoute = (index: number, patch: Partial<NonNullable<FlowNodeConfig['languageRoutes']>[number]>) => {
        const next = [...routes];
        next[index] = { ...next[index], ...patch };
        updateConfig({ languageRoutes: next });
      };

      const toggleRouteLanguage = (index: number, code: string) => {
        const current = routes[index]?.languages || [];
        const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
        updateRoute(index, { languages: next });
      };

      const languageLabel = (codes: string[]) =>
        codes.length === 0
          ? t('flows.checkLanguage.selectLanguages')
          : codes.map((c) => CHECK_LANGUAGE_OPTIONS.find((o) => o.code === c)?.label || c).join(', ');

      return (
        <>
          <View style={styles.field}>
            <InterpolatableInput
              label={t('flows.checkLanguage.inputLabel')}
              placeholder={t('flows.checkLanguage.inputPlaceholder')}
              value={cfg.languageInputTemplate || ''}
              onChangeText={(text) => updateConfig({ languageInputTemplate: text })}
              size="sm"
              variables={availableVariables}
            />
            <Text style={styles.helperText}>{t('flows.checkLanguage.inputHint')}</Text>
            {errors.languageInputTemplate && <Text style={styles.errorText}>{errors.languageInputTemplate}</Text>}
          </View>

          <View style={styles.field}>
            <Input
              label={t('flows.checkLanguage.saveResultAs')}
              value={cfg.saveResultAs || ''}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder={t('dataLists.flowNode.saveResultAsPlaceholder')}
              size="sm"
            />
          </View>

          <View style={styles.field}>
            <View style={styles.buttonsHeader}>
              <Text style={styles.label}>{t('flows.checkLanguage.routesLabel')}</Text>
              <TouchableOpacity onPress={() => updateConfig({ languageRoutes: [...routes, { id: genId('lang'), languages: [] }] })}>
                <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
            </View>
            {errors.languageRoutes && <Text style={styles.errorText}>{errors.languageRoutes}</Text>}
            {routes.map((route, index) => (
              <View key={route.id} style={styles.buttonRow}>
                <Input value={route.name || ''} onChangeText={(text) => updateRoute(index, { name: text.slice(0, 60) || undefined })} placeholder={`Route ${index + 1} name (optional), e.g. Arabic speakers`} size="sm" />
                <View style={styles.buttonRowTop}>
                  <View style={{ flex: 1 }}>
                    <TouchableOpacity style={styles.selectButton} onPress={() => setNodePickerFor(`lang-langs:${route.id}`)}>
                      <Text style={[styles.selectButtonText, route.languages.length === 0 && styles.selectButtonPlaceholder]} numberOfLines={1}>
                        {languageLabel(route.languages)}
                      </Text>
                      <Ionicons name="chevron-down" size={18} color={colors.textLight} />
                    </TouchableOpacity>
                  </View>
                  <TouchableOpacity
                    onPress={() => updateConfig({ languageRoutes: routes.filter((r) => r.id !== route.id) })}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    style={styles.removeButtonIcon}
                  >
                    <Ionicons name="close-circle" size={20} color={colors.error} />
                  </TouchableOpacity>
                </View>
                {renderNextNodePicker(t('flows.checkLanguage.routeNextNode'), route.nextNodeId, (nextNodeId) => updateRoute(index, { nextNodeId }), `lang-next:${route.id}`)}

                <Modal visible={nodePickerFor === `lang-langs:${route.id}`} transparent animationType="slide" onRequestClose={() => setNodePickerFor(null)}>
                  <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                      <View style={styles.modalHeader}>
                        <Text style={styles.modalTitle}>{t('flows.checkLanguage.selectLanguages')}</Text>
                        <TouchableOpacity onPress={() => setNodePickerFor(null)}>
                          <Ionicons name="close" size={24} color={colors.textDark} />
                        </TouchableOpacity>
                      </View>
                      <FlatList
                        data={CHECK_LANGUAGE_OPTIONS}
                        keyExtractor={(item) => item.code}
                        renderItem={({ item }) => {
                          const selected = route.languages.includes(item.code);
                          return (
                            <TouchableOpacity style={styles.option} onPress={() => toggleRouteLanguage(index, item.code)}>
                              <Text style={[styles.optionLabel, selected && { color: colors.primary, fontFamily: typography.fontFamily.semibold }]}>{item.label}</Text>
                              {selected && <Ionicons name="checkmark" size={18} color={colors.primary} />}
                            </TouchableOpacity>
                          );
                        }}
                      />
                    </View>
                  </View>
                </Modal>
              </View>
            ))}
          </View>

          {renderNextNodePicker(t('flows.checkLanguage.defaultRoute'), cfg.languageDefaultNextNodeId, (nextNodeId) => updateConfig({ languageDefaultNextNodeId: nextNodeId }), 'lang-default')}
        </>
      );
    }

    case 'generate_text': {
      const contentType = cfg.generateContentType || 'numbers';
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>Content Type</Text>
            <View style={styles.actionTypeRow}>
              {([
                { value: 'text', label: 'Letters' },
                { value: 'numbers', label: 'Numbers' },
                { value: 'mixed', label: 'Mixed' },
              ] as const).map((opt) => (
                <TouchableOpacity
                  key={opt.value}
                  style={[styles.actionTypeOption, contentType === opt.value && styles.actionTypeOptionActive]}
                  onPress={() => updateConfig({ generateContentType: opt.value })}
                >
                  <Text style={[styles.actionTypeOptionText, contentType === opt.value && styles.actionTypeOptionTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <View style={styles.field}>
            <Input
              label="Length"
              value={cfg.generateLength != null ? String(cfg.generateLength) : ''}
              onChangeText={(text) => {
                const n = parseInt(text.replace(/[^0-9]/g, ''), 10);
                updateConfig({ generateLength: Number.isNaN(n) ? undefined : Math.min(Math.max(n, 1), 64) });
              }}
              placeholder="6"
              size="sm"
              keyboardType="numeric"
              error={errors.generateLength}
            />
          </View>
          <View style={styles.field}>
            <InterpolatableInput
              label="Prefix (Optional)"
              value={cfg.generatePrefix || ''}
              onChangeText={(text) => updateConfig({ generatePrefix: text })}
              placeholder="e.g. REF-"
              size="sm"
              variables={availableVariables}
            />
            <Text style={styles.helperText}>Added before the generated content — doesn't count against Length.</Text>
          </View>
          <View style={styles.field}>
            <Input
              label="Save Result As *"
              value={cfg.saveResultAs || ''}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder="e.g. GeneratedCode"
              size="sm"
              error={errors.saveResultAs}
            />
          </View>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'send_otp': {
      const otpMode = cfg.otpMode || 'template';
      const selectedTemplate = otpTemplates.find((t) => t._id === cfg.otpTemplateId);
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>Send Via</Text>
            <View style={styles.actionTypeRow}>
              {([
                { value: 'template', label: 'Approved Template' },
                { value: 'freeform', label: 'Freeform Message' },
              ] as const).map((opt) => (
                <TouchableOpacity
                  key={opt.value}
                  style={[styles.actionTypeOption, otpMode === opt.value && styles.actionTypeOptionActive]}
                  onPress={() => updateConfig({ otpMode: opt.value })}
                >
                  <Text style={[styles.actionTypeOptionText, otpMode === opt.value && styles.actionTypeOptionTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.field}>
            <InterpolatableInput
              label="Code Value *"
              value={cfg.otpCodeTemplate || ''}
              onChangeText={(text) => updateConfig({ otpCodeTemplate: text })}
              placeholder="e.g. {{GenerateCode.saveResultAt}}"
              size="sm"
              variables={availableVariables}
              error={errors.otpCodeTemplate}
            />
            <Text style={styles.helperText}>Usually a Generate Text node's result — insert its variable here.</Text>
          </View>

          {otpMode === 'template' ? (
            <View style={styles.field}>
              <Text style={styles.label}>Authentication Template *</Text>
              <TouchableOpacity style={styles.selectButton} onPress={() => setOtpTemplatePickerOpen(true)}>
                <Text style={[styles.selectButtonText, !selectedTemplate && styles.selectButtonPlaceholder]}>
                  {selectedTemplate ? `${selectedTemplate.templateName} (${selectedTemplate.language})` : 'Select an approved template'}
                </Text>
                <Ionicons name="chevron-down" size={20} color={colors.textLight} />
              </TouchableOpacity>
              {errors.otpTemplateId && <Text style={styles.errorText}>{errors.otpTemplateId}</Text>}
              <Text style={styles.helperText}>Only APPROVED Authentication-category templates are listed — Meta requires OTP messages to use one.</Text>
              {renderSimpleListModal(
                otpTemplatePickerOpen,
                () => setOtpTemplatePickerOpen(false),
                otpTemplates.map((t) => ({ id: t._id, label: `${t.templateName} (${t.language})` })),
                (id) => {
                  const t = otpTemplates.find((x) => x._id === id);
                  updateConfig({ otpTemplateId: id, otpTemplateName: t?.templateName, otpTemplateLanguage: t?.language });
                },
                loadingOptions,
                'Select Template',
                'No approved authentication templates — submit one for review in Templates first.',
              )}
            </View>
          ) : (
            <>
              <View style={styles.field}>
                <InterpolatableInput
                  label="Message Body *"
                  value={cfg.otpBodyTemplate || ''}
                  onChangeText={(text) => updateConfig({ otpBodyTemplate: text })}
                  placeholder="e.g. {{code}} is your verification code."
                  size="sm"
                  multiline
                  variables={availableVariables}
                  error={errors.otpBodyTemplate}
                />
                <Text style={styles.helperText}>{'{{code}} resolves to the Code Value above.'}</Text>
              </View>
              <View style={styles.field}>
                <Input
                  label="Button Text *"
                  value={cfg.otpButtonText || ''}
                  onChangeText={(text) => updateConfig({ otpButtonText: text })}
                  placeholder="e.g. Copy Code"
                  size="sm"
                  maxLength={20}
                  error={errors.otpButtonText}
                />
                <Text style={styles.helperText}>Shown as text only — not a real clipboard action (WhatsApp has no freeform copy button).</Text>
              </View>
            </>
          )}

          <View style={styles.field}>
            <Input
              label="Expires In (Minutes)"
              value={cfg.otpExpiryMinutes != null ? String(cfg.otpExpiryMinutes) : ''}
              onChangeText={(text) => {
                const n = parseInt(text.replace(/[^0-9]/g, ''), 10);
                updateConfig({ otpExpiryMinutes: Number.isNaN(n) ? undefined : n });
              }}
              placeholder="5"
              size="sm"
              keyboardType="numeric"
            />
            <Text style={styles.helperText}>Informational only — whatever validates the code later must check its age itself.</Text>
          </View>

          <View style={styles.field}>
            <Input
              label="Save Result As (Optional)"
              value={cfg.saveResultAs || ''}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder="e.g. SentOtp"
              size="sm"
            />
          </View>

          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'pause':
      return (
        <>
          <Text style={styles.helperText}>The flow stops here for this contact. A team member can resume it from Dashboard → Flow runs → Paused; it then continues with the step below.</Text>
          {renderNextNodePicker('After resume', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'ai_step': {
      const mode = cfg.aiMode || 'classify';
      const categories = cfg.aiCategories || [];
      const fields = cfg.aiFields || [];
      const setCategory = (id: string, patch: Partial<{ label: string; description: string; nextNodeId: string }>) =>
        updateConfig({ aiCategories: categories.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
      const setField = (id: string, patch: Partial<{ name: string; description: string }>) =>
        updateConfig({ aiFields: fields.map((f) => (f.id === id ? { ...f, ...patch } : f)) });
      return (
        <>
          <Text style={styles.helperText}>Uses the AI model connected in Settings → AI. If none is connected or it fails, the flow takes the "AI unavailable" path.</Text>
          {renderActAs()}
          <View style={styles.field}>
            <Text style={styles.label}>The AI should</Text>
            {renderChoice([{ value: 'classify', label: 'Sort the message' }, { value: 'extract', label: 'Pick out details' }, { value: 'reply', label: 'Write a reply' }], mode, (value) => updateConfig({ aiMode: value }))}
          </View>
          <View style={styles.field}>
            <InterpolatableInput
              label="Read this"
              value={cfg.aiInputTemplate ?? '{{lastMessage}}'}
              onChangeText={(text) => updateConfig({ aiInputTemplate: text })}
              placeholder="{{lastMessage}}"
              size="sm"
              variables={availableVariables}
            />
            <Text style={styles.helperText}>
              {mode === 'reply'
                ? 'Replies always read the whole conversation and the customer\'s latest message. Anything else written here (e.g. {{contact.name}}) is given to the AI as background, not as the customer\'s words. Leave it as {{lastMessage}} unless you need that.'
                : '{{lastMessage}} is the contact\'s latest message.'}
            </Text>
          </View>
          <View style={styles.field}>
            <Input
              label="Instructions (Optional)"
              value={cfg.aiInstructions || ''}
              onChangeText={(text) => updateConfig({ aiInstructions: text.slice(0, 2000) })}
              placeholder={mode === 'reply' ? 'e.g. Answer delivery questions; offer the menu link. (Applied to EVERY reply — put one-time greetings in a message step instead.)' : 'e.g. "Wholesale" means 50 pieces or more'}
              size="sm"
              multiline
            />
          </View>

          {mode === 'classify' && (
            <View style={styles.agentHint}>
              <Ionicons name="chatbubbles-outline" size={18} color={colors.primary as string} />
              <View style={{ flex: 1 }}>
                <Text style={styles.agentHintTitle}>One decision on this one message</Text>
                <Text style={styles.helperText}>
                  Sort reads only the current message and moves on — a greeting fits no category, and the run ends. To let the AI talk with the customer until it knows what they want, then continue to the matching step, use an AI Agent.
                </Text>
                <TouchableOpacity style={styles.agentHintButton} onPress={() => onChange(aiStepToAgent(node))}>
                  <Text style={styles.agentHintButtonText}>Convert to AI Agent (keeps your categories as exits)</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
          {mode === 'classify' && (
            <View style={styles.field}>
              <View style={styles.buttonsHeader}>
                <Text style={styles.label}>Categories</Text>
                <TouchableOpacity accessibilityLabel="Add category" onPress={() => updateConfig({ aiCategories: [...categories, { id: genId('cat'), label: '' }] })}>
                  <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                </TouchableOpacity>
              </View>
              {errors.aiCategories && <Text style={styles.errorText}>{errors.aiCategories}</Text>}
              {categories.map((category, index) => (
                <View key={category.id} style={styles.buttonRow}>
                  <View style={styles.buttonRowTop}>
                    <View style={{ flex: 1 }}>
                      <Input value={category.label} onChangeText={(text) => setCategory(category.id, { label: text.slice(0, 60) })} placeholder={`Category ${index + 1}, e.g. New order`} size="sm" />
                    </View>
                    <TouchableOpacity accessibilityLabel="Remove category" onPress={() => updateConfig({ aiCategories: categories.filter((c) => c.id !== category.id) })} style={styles.removeButtonIcon}>
                      <Ionicons name="close-circle" size={20} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  <Input value={category.description || ''} onChangeText={(text) => setCategory(category.id, { description: text.slice(0, 200) })} placeholder="When to pick it (optional)" size="sm" />
                  {renderNextNodePicker('Then', category.nextNodeId, (nextNodeId) => setCategory(category.id, { nextNodeId }), `ai-cat:${category.id}`)}
                </View>
              ))}
            </View>
          )}

          {mode === 'extract' && (
            <View style={styles.field}>
              <View style={styles.buttonsHeader}>
                <Text style={styles.label}>Details to pick out</Text>
                <TouchableOpacity accessibilityLabel="Add detail" onPress={() => updateConfig({ aiFields: [...fields, { id: genId('fld'), name: '' }] })}>
                  <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                </TouchableOpacity>
              </View>
              {errors.aiFields && <Text style={styles.errorText}>{errors.aiFields}</Text>}
              {fields.map((field) => (
                <View key={field.id} style={styles.buttonRow}>
                  <View style={styles.buttonRowTop}>
                    <View style={{ flex: 1 }}>
                      <Input value={field.name} onChangeText={(text) => setField(field.id, { name: text.replace(/[^a-zA-Z0-9_ ]/g, '').slice(0, 40) })} placeholder="Name, e.g. city" size="sm" />
                    </View>
                    <TouchableOpacity accessibilityLabel="Remove detail" onPress={() => updateConfig({ aiFields: fields.filter((f) => f.id !== field.id) })} style={styles.removeButtonIcon}>
                      <Ionicons name="close-circle" size={20} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  <Input value={field.description || ''} onChangeText={(text) => setField(field.id, { description: text.slice(0, 200) })} placeholder="What it is, e.g. delivery city" size="sm" />
                </View>
              ))}
              <Text style={styles.helperText}>A detail the message doesn't mention stays empty — the AI is told never to guess.</Text>
            </View>
          )}

          {mode === 'reply' && (
            <View style={styles.field}>
              <Text style={styles.label}>Send the reply to the contact</Text>
              {renderChoice([{ value: 'yes', label: 'Send it' }, { value: 'no', label: 'Only save it' }], cfg.aiSendReply ? 'yes' : 'no', (value) => updateConfig({ aiSendReply: value === 'yes' }))}
              <Text style={styles.helperText}>Uses the conversation assistant: business knowledge, recent messages, products, vendors, categories, sizes and quantities allowed by your plan. Product and size choices are sent as WhatsApp buttons/lists or Messenger quick replies, matching the customer's language. Orders and quotations still need team review.</Text>
            </View>
          )}

          <View style={styles.field}>
            <Input
              label={mode === 'extract' ? 'Save details as *' : mode === 'reply' ? 'Save reply as' : 'Save category as (Optional)'}
              value={cfg.saveResultAs || ''}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder={mode === 'extract' ? 'e.g. details' : mode === 'reply' ? 'e.g. aiReply' : 'e.g. intent'}
              size="sm"
              error={errors.saveResultAs}
            />
          </View>
          {mode !== 'classify' && renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
          {errors.nextNodeId && <Text style={styles.errorText}>{errors.nextNodeId}</Text>}
          {renderNextNodePicker(mode === 'classify' ? 'No category fits / AI unavailable' : 'AI unavailable', cfg.aiDefaultNextNodeId, (nextNodeId) => updateConfig({ aiDefaultNextNodeId: nextNodeId }), 'ai-fallback')}
        </>
      );
    }

    case 'wait': {
      const mode = cfg.waitMode || 'duration';
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label, value) => ({ value, label }));
      return (
        <>
          <Text style={styles.label}>Wait</Text>
          {renderChoice([{ value: 'duration', label: 'For a time' }, { value: 'until_time', label: 'Until a time of day' }, { value: 'until_open', label: 'Until we open' }], mode,
            (value) => updateConfig({ waitMode: value }))}
          {mode === 'duration' && (
            <View style={styles.field}>
              <View style={styles.buttonRowTop}>
                <View style={{ flex: 1 }}>
                  <Input
                    label="How long"
                    value={cfg.waitAmount ? String(cfg.waitAmount) : ''}
                    onChangeText={(text) => updateConfig({ waitAmount: parseInt(text.replace(/[^0-9]/g, ''), 10) || undefined })}
                    keyboardType="number-pad"
                    placeholder="1"
                    size="sm"
                  />
                </View>
              </View>
              {renderChoice([{ value: 'minutes', label: 'Minutes' }, { value: 'hours', label: 'Hours' }, { value: 'days', label: 'Days' }], cfg.waitUnit || 'minutes',
                (value) => updateConfig({ waitUnit: value }))}
              {errors.waitAmount && <Text style={styles.errorText}>{errors.waitAmount}</Text>}
            </View>
          )}
          {mode === 'until_time' && (
            <View style={styles.field}>
              <Input
                label="Time (HH:mm)"
                value={cfg.waitTime || ''}
                onChangeText={(text) => updateConfig({ waitTime: text.replace(/[^0-9:]/g, '').slice(0, 5) || undefined })}
                placeholder="09:00"
                size="sm"
              />
              {errors.waitTime && <Text style={styles.errorText}>{errors.waitTime}</Text>}
              <Text style={styles.label}>Only on</Text>
              {renderChips(days, cfg.waitDaysOfWeek, (next) => updateConfig({ waitDaysOfWeek: next.length ? next : undefined }))}
              <Text style={styles.helperText}>The next time the clock shows this time, in your business timezone. No days picked = any day.</Text>
            </View>
          )}
          {mode === 'until_open' && (
            <Text style={styles.helperText}>Waits until the next opening in your working hours (Settings → Business profile). If you are open now, the flow goes straight on.</Text>
          )}
          <Text style={styles.helperText}>The longest wait is 30 days; the flow continues within a minute of the time. On WhatsApp, a message more than 24 hours after the customer last wrote may not be delivered unless it is a template.</Text>
          {renderNextNodePicker('After the wait', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
          {renderNextNodePicker('If they reply during the wait (optional)', cfg.waitReplyNextNodeId, (nextNodeId) => updateConfig({ waitReplyNextNodeId: nextNodeId }), 'reply')}
          <Text style={styles.helperText}>Without this, replies during the wait are left for your team and the flow keeps waiting.</Text>
        </>
      );
    }

    case 'ai_agent': {
      const exits = cfg.agentExits || [];
      const setExit = (id: string, patch: Partial<FlowAgentExit>) => updateConfig({ agentExits: exits.map((e) => (e.id === id ? { ...e, ...patch } : e)) });
      const usedKinds = new Set(exits.map((e) => e.kind));
      return (
        <>
          <Text style={styles.helperText}>The AI holds the conversation: it answers every customer message here — products, sizes, cart, questions — like the conversation assistant, and the run stays on this step. When the conversation reaches one of the exits below, the run continues there (e.g. Create Order, Assign to User, another flow).</Text>
          {renderActAs()}
          <View style={styles.field}>
            <Input
              label="Instructions (optional)"
              value={cfg.aiInstructions || ''}
              onChangeText={(text) => updateConfig({ aiInstructions: text.slice(0, 2000) })}
              placeholder="e.g. You sell bottled water to cafés. Always offer the 12-pack for orders over 20 bottles."
              size="sm"
              multiline
            />
            <Text style={styles.helperText}>How to behave — applied to every reply. Put a one-time welcome in a Send Message step before this one.</Text>
          </View>
          <View style={styles.field}>
            <View style={styles.buttonsHeader}>
              <Text style={styles.label}>Exits — when the AI is done</Text>
              <TouchableOpacity accessibilityLabel="Add exit" onPress={() => updateConfig({ agentExits: [...exits, { id: genId('exit'), kind: 'custom', label: '' }] })}>
                <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
            </View>
            {errors.agentExits && <Text style={styles.errorText}>{errors.agentExits}</Text>}
            {exits.map((exit, index) => {
              const kinds = AGENT_EXIT_KINDS.filter((k) => k.kind === 'custom' || k.kind === exit.kind || !usedKinds.has(k.kind));
              return (
                <View key={exit.id} style={styles.buttonRow}>
                  <View style={styles.buttonRowTop}>
                    <View style={{ flex: 1 }}>
                      <Input value={exit.label} onChangeText={(text) => setExit(exit.id, { label: text.slice(0, 60) })} placeholder={`Exit ${index + 1} name`} size="sm" />
                    </View>
                    <TouchableOpacity accessibilityLabel="Remove exit" onPress={() => updateConfig({ agentExits: exits.filter((e) => e.id !== exit.id) })} style={styles.removeButtonIcon}>
                      <Ionicons name="close-circle" size={20} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  {renderChoice(kinds.map((k) => ({ value: k.kind, label: k.label })), exit.kind, (kind) => {
                    const named = AGENT_EXIT_KINDS.find((k) => k.kind === exit.kind)?.label;
                    const next = AGENT_EXIT_KINDS.find((k) => k.kind === kind);
                    setExit(exit.id, { kind, ...(!exit.label || exit.label === named ? { label: kind === 'custom' ? '' : next?.label || '' } : {}) });
                  })}
                  <Text style={styles.helperText}>{AGENT_EXIT_KINDS.find((k) => k.kind === exit.kind)?.hint}</Text>
                  {exit.kind === 'custom' && (
                    <Input
                      value={exit.description || ''}
                      onChangeText={(text) => setExit(exit.id, { description: text.slice(0, 400) })}
                      placeholder="When to take it, e.g. The customer complains about a late delivery"
                      size="sm"
                      multiline
                    />
                  )}
                  {errors[`exit_${index}`] && <Text style={styles.errorText}>{errors[`exit_${index}`]}</Text>}
                  {renderNextNodePicker('Then', exit.nextNodeId, (nextNodeId) => setExit(exit.id, { nextNodeId }), `exit:${exit.id}`)}
                </View>
              );
            })}
          </View>
          <View style={styles.field}>
            <Input
              label="If the customer goes silent for (minutes, optional)"
              value={cfg.agentSilenceMinutes ? String(cfg.agentSilenceMinutes) : ''}
              onChangeText={(text) => updateConfig({ agentSilenceMinutes: parseInt(text.replace(/[^0-9]/g, ''), 10) || undefined })}
              keyboardType="number-pad"
              placeholder="e.g. 60"
              size="sm"
            />
            {errors.agentSilence && <Text style={styles.errorText}>{errors.agentSilence}</Text>}
          </View>
          {!!cfg.agentSilenceMinutes && renderNextNodePicker('Then (went silent)', cfg.agentSilenceNextNodeId, (nextNodeId) => updateConfig({ agentSilenceNextNodeId: nextNodeId }), 'silence')}
          <View style={styles.field}>
            <Input
              label="Most replies before handing on"
              value={String(cfg.agentMaxTurns || 30)}
              onChangeText={(text) => updateConfig({ agentMaxTurns: Math.min(200, parseInt(text.replace(/[^0-9]/g, ''), 10) || 30) })}
              keyboardType="number-pad"
              size="sm"
            />
          </View>
          {renderNextNodePicker('Then (too many replies)', cfg.agentMaxTurnsNextNodeId, (nextNodeId) => updateConfig({ agentMaxTurnsNextNodeId: nextNodeId }), 'maxturns')}
          {renderNextNodePicker('If the AI is unavailable (optional)', cfg.aiDefaultNextNodeId, (nextNodeId) => updateConfig({ aiDefaultNextNodeId: nextNodeId }), 'fallback')}
          <Text style={styles.helperText}>Without an "unavailable" path the AI keeps the conversation and tries again on the next message.</Text>
          <View style={styles.field}>
            <Input label="Save as" value={cfg.saveResultAs ?? 'agent'} onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })} placeholder="agent" size="sm" />
            <Text style={styles.helperText}>{`Later steps get {{${cfg.saveResultAs || 'agent'}.cart}}, .total, .summary, .exit, .language`}</Text>
          </View>
        </>
      );
    }

    case 'create_order':
    case 'create_quotation': {
      const isOrder = node.type === 'create_order';
      // Manage Quotation may allow several actions: the AI agent's conversation picks one.
      const multi = !isOrder && (cfg.recordOperations?.length || 0) > 1;
      const selectedOps: string[] = multi ? cfg.recordOperations! : [cfg.recordOperation === 'auto' ? 'create' : cfg.recordOperation || 'create'];
      const op = multi ? 'create' : selectedOps[0];
      const toggleOp = (value: string) => {
        const combinable = ['create', 'update', 'delete', 'convert'];
        let next = selectedOps.includes(value) ? selectedOps.filter((v) => v !== value)
          : combinable.includes(value) ? [...selectedOps.filter((v) => combinable.includes(v)), value] : [value];
        if (!next.length) next = [value];
        updateConfig(next.length > 1
          ? { recordOperations: next, recordOperation: 'create' }
          : { recordOperations: undefined, recordOperation: next[0] as any, ...(next[0] === 'create' ? {} : { recordTarget: cfg.recordTarget || 'latest' }) });
      };
      const ops = isOrder
        ? [{ value: 'create', label: 'Create' }, { value: 'find', label: 'Find' }, { value: 'update', label: 'Update' }, { value: 'cancel', label: 'Cancel' }]
        : [{ value: 'create', label: 'Create' }, { value: 'find', label: 'Find' }, { value: 'update', label: 'Update' }, { value: 'convert', label: 'Convert to order' }, { value: 'delete', label: 'Delete' }];
      const orderStatuses = ['pending', 'placed', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
      const quotationStatuses = ['sent', 'accepted', 'rejected', 'expired'];
      const savedAs = cfg.saveResultAs || (isOrder ? 'order' : 'quotation');
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>{isOrder ? 'Action' : 'Actions'}</Text>
            {isOrder ? renderChoice(ops as any, op as any, (value: any) => updateConfig({ recordOperation: value, ...(value === 'create' ? {} : { recordTarget: cfg.recordTarget || 'latest' }) })) : (
              <View style={styles.chipRow}>
                {ops.map((o) => {
                  const on = selectedOps.includes(o.value);
                  return (
                    <TouchableOpacity key={o.value} accessibilityRole="checkbox" accessibilityState={{ checked: on }} style={[styles.chip, on && styles.actionTypeOptionActive]} onPress={() => toggleOp(o.value)}>
                      <Text style={[styles.actionTypeOptionText, on && styles.actionTypeOptionTextActive]}>{o.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
            {!isOrder && (
              <Text style={styles.helperText}>
                {multi
                  ? `The AI Agent's conversation decides which of these happens: a new quotation from the cart, the changed quotation updated, a cancelled one deleted, or an accepted one turned into an order. Use it after an AI Agent's "Wants a quotation" exit.`
                  : 'Pick several (Create, Update, Delete, Convert to order) to let the conversation decide which one happens.'}
              </Text>
            )}
          </View>
          {op === 'create' ? (
            <View style={styles.field}>
              <Input
                label="Products from"
                value={cfg.recordSource ?? 'agent.cart'}
                onChangeText={(text) => updateConfig({ recordSource: text.replace(/[{}\s]/g, '') })}
                placeholder="agent.cart"
                size="sm"
                error={errors.recordSource}
              />
              <Text style={styles.helperText}>{`The AI Agent's cart ({{agent.cart}}) — or any variable holding [{ productId, variantId, quantity }]. Prices are read from the catalog now.`}</Text>
            </View>
          ) : (
            <View style={styles.field}>
              <Text style={styles.label}>{isOrder ? 'Which order' : 'Which quotation'}</Text>
              {renderChoice([{ value: 'latest', label: isOrder ? 'Their latest order' : 'Their latest quotation' }, { value: 'number', label: 'By number' }],
                cfg.recordTarget || 'latest', (value) => updateConfig({ recordTarget: value }))}
              {cfg.recordTarget === 'number' && (
                <InterpolatableInput value={cfg.recordNumber || ''} onChangeText={(text) => updateConfig({ recordNumber: text })}
                  placeholder={isOrder ? 'e.g. {{details.orderNumber}} or ORD-…' : 'e.g. {{details.quotationNumber}}'} size="sm" variables={availableVariables} error={errors.recordNumber} />
              )}
              <Text style={styles.helperText}>Only this customer's own {isOrder ? 'orders' : 'quotations'} are ever found — never someone else's, whatever number is typed.</Text>
            </View>
          )}
          {op === 'update' && (
            <View style={styles.field}>
              <Text style={styles.label}>New status</Text>
              {renderChoice([{ value: '', label: 'Keep' }, ...(isOrder ? orderStatuses : quotationStatuses).map((v) => ({ value: v, label: v }))] as any,
                (cfg.recordStatus || '') as any, (value: any) => updateConfig({ recordStatus: value || undefined }))}
              {errors.recordStatus && <Text style={styles.errorText}>{errors.recordStatus}</Text>}
              {isOrder && (
                <>
                  <Text style={[styles.label, { marginTop: spacing.sm }]}>Payment</Text>
                  {renderChoice([{ value: '', label: 'Keep' }, { value: 'unpaid', label: 'unpaid' }, { value: 'partial', label: 'partial' }, { value: 'paid', label: 'paid' }, { value: 'refunded', label: 'refunded' }] as any,
                    (cfg.recordPaymentStatus || '') as any, (value: any) => updateConfig({ recordPaymentStatus: value || undefined }))}
                </>
              )}
            </View>
          )}
          {!isOrder && (op === 'create' || op === 'update') && (
            <View style={styles.field}>
              <Input
                label={op === 'create' ? 'Valid for (days)' : 'Extend validity to (days from now, optional)'}
                value={cfg.quotationValidDays ? String(cfg.quotationValidDays) : ''}
                onChangeText={(text) => updateConfig({ quotationValidDays: parseInt(text.replace(/[^0-9]/g, ''), 10) || undefined })}
                keyboardType="number-pad"
                placeholder="7"
                size="sm"
              />
            </View>
          )}
          {(op === 'create' || op === 'update') && (
            <View style={styles.field}>
              <InterpolatableInput label={op === 'create' ? 'Notes (optional)' : 'Add a note (optional)'} value={cfg.recordNotes || ''} onChangeText={(text) => updateConfig({ recordNotes: text })}
                placeholder={'e.g. {{agent.summary}}'} size="sm" variables={availableVariables} />
            </View>
          )}
          {op !== 'delete' && (
            <View style={styles.field}>
              <Text style={styles.label}>{op === 'convert' ? 'Send the new order to the customer' : 'Send to the customer'}</Text>
              {renderChoice([{ value: 'details', label: 'Details message' }, { value: 'pdf', label: 'PDF' }, { value: 'both', label: 'Both' }, { value: 'none', label: "Don't send" }],
                cfg.shareMode || (op === 'create' ? (isOrder ? 'details' : 'pdf') : 'none'), (value) => updateConfig({ shareMode: value }))}
            </View>
          )}
          <View style={styles.field}>
            <Input label="Save as" value={cfg.saveResultAs ?? (isOrder ? 'order' : 'quotation')} onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })} size="sm" />
            <Text style={styles.helperText}>{`Later steps get {{${savedAs}.number}}, .status, .total, .items${isOrder ? ', .payment' : op === 'convert' ? ', .orderNumber' : ', .validUntil'}`}</Text>
          </View>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
          {op !== 'create' && renderNextNodePicker('If not found (optional)', cfg.recordNotFoundNextNodeId, (nextNodeId) => updateConfig({ recordNotFoundNextNodeId: nextNodeId }), 'notfound')}
          {op !== 'create' && !cfg.recordNotFoundNextNodeId && <Text style={styles.helperText}>Without a "not found" path, the run records an error and goes on to "Then".</Text>}
        </>
      );
    }

    case 'add_to_vendor':
    case 'create_contact': {
      const isVendor = node.type === 'add_to_vendor';
      const f = cfg.recordFields || {};
      const setField = (key: keyof NonNullable<FlowNodeConfig['recordFields']>, value: string) => updateConfig({ recordFields: { ...f, [key]: value } });
      const vendorOp = isVendor ? cfg.recordOperation || 'create' : 'create';
      return (
        <>
          {isVendor && (
            <View style={styles.field}>
              <Text style={styles.label}>Action</Text>
              {renderChoice([{ value: 'create', label: 'Add' }, { value: 'find', label: 'Find' }, { value: 'update', label: 'Update' }, { value: 'remove', label: 'Remove' }] as any,
                vendorOp as any, (value: any) => updateConfig({ recordOperation: value }))}
            </View>
          )}
          <Text style={styles.helperText}>{!isVendor
            ? 'Creates a new contact — for example someone the customer refers, with details picked out earlier by an AI step.'
            : vendorOp === 'create' ? 'Registers this contact as a vendor (supplier), linked to the contact. If they are already a vendor, nothing new is created.'
              : vendorOp === 'update' ? "Updates the vendor linked to this contact — only the fields you fill change."
                : vendorOp === 'remove' ? 'Removes the vendor linked to this contact.' : 'Finds the vendor linked to this contact.'}</Text>
          {(vendorOp === 'create' || vendorOp === 'update') && (<>
          <View style={styles.field}>
            <InterpolatableInput label={isVendor ? 'Vendor name' : 'Name *'} value={f.name || ''} onChangeText={(text) => setField('name', text)}
              placeholder={isVendor ? '{{contact.name}}' : 'e.g. {{details.name}}'} size="sm" variables={availableVariables} error={errors.recordName} />
          </View>
          <View style={styles.field}>
            <InterpolatableInput label={isVendor ? 'Phone (optional)' : 'Phone'} value={f.phone || ''} onChangeText={(text) => setField('phone', text)}
              placeholder={isVendor ? "The contact's number" : 'e.g. {{details.phone}}'} size="sm" variables={availableVariables} error={errors.recordPhone} />
          </View>
          <View style={styles.field}>
            <InterpolatableInput label="Email (optional)" value={f.email || ''} onChangeText={(text) => setField('email', text)} size="sm" variables={availableVariables} />
          </View>
          {isVendor ? (
            <View style={styles.field}>
              <InterpolatableInput label="Notes (optional)" value={f.notes || ''} onChangeText={(text) => setField('notes', text)} placeholder="e.g. {{agent.summary}}" size="sm" variables={availableVariables} />
            </View>
          ) : (
            <View style={styles.field}>
              <InterpolatableInput label="Tags (optional, comma separated)" value={f.tags || ''} onChangeText={(text) => setField('tags', text)} placeholder="e.g. referral" size="sm" variables={availableVariables} />
            </View>
          )}
          </>)}
          <View style={styles.field}>
            <Input label="Save as" value={cfg.saveResultAs ?? (isVendor ? 'vendor' : 'newContact')} onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })} size="sm" />
          </View>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
          {isVendor && vendorOp !== 'create' && renderNextNodePicker('If not a vendor (optional)', cfg.recordNotFoundNextNodeId, (nextNodeId) => updateConfig({ recordNotFoundNextNodeId: nextNodeId }), 'notfound')}
        </>
      );
    }

    case 'add_to_list':
    case 'remove_from_list': {
      const listOp = node.type === 'remove_from_list' ? 'remove' : cfg.recordOperation === 'remove' || cfg.recordOperation === 'check' ? cfg.recordOperation : 'add';
      const adding = listOp === 'add';
      const mappings = cfg.dataListValueMappings || [];
      const setMapping = (id: string, patch: Partial<{ columnId: string; valueTemplate: string }>) =>
        updateConfig({ dataListValueMappings: mappings.map((m) => (m.id === id ? { ...m, ...patch } : m)) });
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>List *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setDataListPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !cfg.dataListId && styles.selectButtonPlaceholder]}>
                {cfg.dataListId ? dataLists.find((l) => l._id === cfg.dataListId)?.name || 'List selected' : 'Select a list'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.dataListId && <Text style={styles.errorText}>{errors.dataListId}</Text>}
            <Text style={styles.helperText}>{adding ? 'Adds this contact once — a contact already on the list is left as is.' : listOp === 'check' ? 'Checks whether this contact is on the list and continues by "On the list" or "Not on the list".' : 'Removes every entry of this contact from the list.'}</Text>
          </View>
          {node.type === 'add_to_list' && (
            <View style={styles.field}>
              <Text style={styles.label}>Action</Text>
              {renderChoice([{ value: 'add', label: 'Add to list' }, { value: 'remove', label: 'Remove from list' }, { value: 'check', label: 'Is on the list?' }] as any,
                listOp as any, (value: any) => updateConfig({ recordOperation: value }))}
            </View>
          )}
          {renderSimpleListModal(dataListPickerOpen, () => setDataListPickerOpen(false), dataLists.map((l) => ({ id: l._id, label: l.name })),
            (id) => updateConfig({ dataListId: id, dataListValueMappings: [] }), loadingOptions, 'Select a list', 'No data lists yet')}
          {adding && !!cfg.dataListId && (
            <View style={styles.field}>
              <View style={styles.buttonsHeader}>
                <Text style={styles.label}>Values (optional)</Text>
                <TouchableOpacity accessibilityLabel="Add value" onPress={() => updateConfig({ dataListValueMappings: [...mappings, { id: genId('map'), columnId: '', valueTemplate: '' }] })}>
                  <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                </TouchableOpacity>
              </View>
              {mappings.map((m) => (
                <View key={m.id} style={styles.buttonRow}>
                  <View style={styles.buttonRowTop}>
                    <TouchableOpacity style={[styles.selectButton, { flex: 1 }]} onPress={() => setListColumnPickerFor(m.id)}>
                      <Text style={[styles.selectButtonText, !m.columnId && styles.selectButtonPlaceholder]} numberOfLines={1}>
                        {selectedListColumns.find((c) => c.id === m.columnId)?.name || 'Select a column'}
                      </Text>
                      <Ionicons name="chevron-down" size={18} color={colors.textLight} />
                    </TouchableOpacity>
                    <TouchableOpacity accessibilityLabel="Remove value" onPress={() => updateConfig({ dataListValueMappings: mappings.filter((x) => x.id !== m.id) })} style={styles.removeButtonIcon}>
                      <Ionicons name="close-circle" size={20} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  <View style={{ marginTop: spacing.xs }}>
                    <InterpolatableInput label="Value" value={m.valueTemplate} onChangeText={(text) => setMapping(m.id, { valueTemplate: text })} size="sm" variables={availableVariables} />
                  </View>
                </View>
              ))}
            </View>
          )}
          {renderSimpleListModal(!!listColumnPickerFor, () => setListColumnPickerFor(null), selectedListColumns.map((c) => ({ id: c.id, label: c.name })),
            (id) => { if (listColumnPickerFor) setMapping(listColumnPickerFor, { columnId: id }); }, false, 'Select a column', 'This list has no columns')}
          {renderNextNodePicker(listOp === 'check' ? 'On the list' : 'Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
          {listOp === 'check' && renderNextNodePicker('Not on the list', cfg.recordNotFoundNextNodeId, (nextNodeId) => updateConfig({ recordNotFoundNextNodeId: nextNodeId }), 'notfound')}
        </>
      );
    }

    case 'send_template': {
      const selected = waTemplates.find((t) => t._id === cfg.waTemplateId);
      const params = cfg.waTemplateParams || [];
      const count = Math.max(selected?.params || 0, params.length);
      return (
        <>
          <Text style={styles.helperText}>WhatsApp allows free messages only within 24 hours of the customer's last message; after that, only approved templates go through.</Text>
          <View style={styles.field}>
            <Text style={styles.label}>Template *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setWaTemplatePickerOpen(true)}>
              <Text style={[styles.selectButtonText, !cfg.waTemplateName && styles.selectButtonPlaceholder]}>
                {cfg.waTemplateName ? `${cfg.waTemplateName} (${cfg.waTemplateLanguage})` : 'Select an approved template'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.waTemplateId && <Text style={styles.errorText}>{errors.waTemplateId}</Text>}
          </View>
          {renderSimpleListModal(waTemplatePickerOpen, () => setWaTemplatePickerOpen(false),
            waTemplates.map((t) => ({ id: t._id, label: `${t.templateName} (${t.language})${t.category ? ` · ${t.category}` : ''}` })),
            (id) => {
              const t = waTemplates.find((x) => x._id === id);
              updateConfig({ waTemplateId: id, waTemplateName: t?.templateName, waTemplateLanguage: t?.language, waTemplateParams: Array.from({ length: t?.params || 0 }, (_, i) => params[i] || ''), waHeaderMediaUrl: undefined, waHeaderFilename: undefined, waHeaderAsset: undefined });
            },
            loadingOptions, 'Select Template', 'No approved templates — submit one in Templates first.')}
          {selected?.headerMedia ? (
            <View style={styles.field}>
              <Text style={styles.label}>{`Header ${selected.headerMedia}`}</Text>
              {/* The conversation template picker's own file / place picker. */}
              <AssetCard
                headerType={selected.headerMedia as any}
                headerAsset={cfg.waHeaderAsset ? { ...(cfg.waHeaderAsset as any), ...(cfg.waHeaderAsset.type === 'location' ? { mediaId: JSON.stringify(cfg.waHeaderAsset) } : {}) } : undefined}
                campaignName="workflow"
                onAssetChange={(asset) => {
                  // Kept as a link (an upload's Meta media id expires) or the place itself.
                  if (asset.type === 'location') {
                    let place: any = {};
                    try { place = JSON.parse(asset.mediaId || '{}'); } catch { /* none */ }
                    updateConfig({ waHeaderAsset: { type: 'location', latitude: place.latitude, longitude: place.longitude, address: place.address } });
                  } else {
                    updateConfig({ waHeaderAsset: { type: asset.type, url: asset.url, ...(asset.filename ? { filename: asset.filename } : {}) } });
                  }
                }}
              />
              {cfg.waHeaderAsset ? (
                <TouchableOpacity onPress={() => updateConfig({ waHeaderAsset: undefined })}>
                  <Text style={[styles.helperText, { color: colors.primary }]}>Use the template's own instead</Text>
                </TouchableOpacity>
              ) : <Text style={styles.helperText}>{`Nothing chosen = the template's own ${selected.headerMedia}.`}</Text>}
              {selected.headerMedia !== 'location' ? (
                <InterpolatableInput label={`Or a link from the workflow`} value={cfg.waHeaderMediaUrl || ''} size="sm" variables={availableVariables}
                  placeholder="e.g. {{order.invoiceUrl}} — used first when set" onChangeText={(text) => updateConfig({ waHeaderMediaUrl: text.trim() || undefined })} />
              ) : null}
              {selected.headerMedia === 'document' ? (
                <InterpolatableInput label="Document file name" value={cfg.waHeaderFilename || ''} size="sm" variables={availableVariables}
                  placeholder="e.g. Invoice {{order.number}}.pdf" onChangeText={(text) => updateConfig({ waHeaderFilename: text || undefined })} />
              ) : null}
            </View>
          ) : null}
          {Array.from({ length: count }, (_, i) => (
            <View key={i} style={styles.field}>
              <InterpolatableInput label={`${({ header: 'Header', body: 'Body', button: 'Button link', footer: 'Footer' } as Record<string, string>)[selected?.sections?.[i + 1] || 'body'] || 'Value'} {{${i + 1}}}`} value={params[i] || ''}
                onChangeText={(text) => { const next = [...params]; next[i] = text; updateConfig({ waTemplateParams: next }); }}
                placeholder={i === 0 ? '{{contact.name}}' : ''} size="sm" variables={availableVariables} />
            </View>
          ))}
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'manage_assignments': {
      const op = cfg.asgOperation || 'find';
      const scope = cfg.asgScope || 'contact';
      const save = cfg.saveResultAs || 'assignments';
      const toggle = (list: string[] | undefined, v: string) => ((list || []).includes(v) ? (list || []).filter((x) => x !== v) : [...(list || []), v]);
      const chip = (on: boolean, label: string, onPress: () => void, key?: string) => (
        <TouchableOpacity key={key || label} style={[styles.chip, on && styles.actionTypeOptionActive]} onPress={onPress}>
          <Text style={[styles.helperText, { marginTop: 0 }, on && { color: colors.primary }]}>{label}</Text>
        </TouchableOpacity>
      );
      const chips = (title: string, options: Array<[string, string]>, selected: string[] | undefined, onChange: (next: string[]) => void, hint?: string) => (
        <View style={styles.field}>
          <Text style={styles.label}>{title}</Text>
          <View style={styles.chipRow}>{options.map(([id, label]) => chip((selected || []).includes(id), label, () => onChange(toggle(selected, id)), id))}</View>
          {hint ? <Text style={styles.helperText}>{hint}</Text> : null}
        </View>
      );
      const pickButton = (label: string, count: number, unit: string, onPress: () => void, error?: string) => (
        <View style={styles.field}>
          <Text style={styles.label}>{label}</Text>
          <TouchableOpacity style={styles.selectButton} onPress={onPress}>
            <Text style={[styles.selectButtonText, !count && styles.selectButtonPlaceholder]}>{count ? `${count} ${unit}${count === 1 ? '' : 's'} selected` : `Select ${unit}s`}</Text>
            <Ionicons name="chevron-down" size={20} color={colors.textLight} />
          </TouchableOpacity>
          {error ? <Text style={styles.errorText}>{error}</Text> : null}
        </View>
      );
      const KINDS: Array<[string, string]> = [['contact', 'Contacts'], ['conversation', 'Conversations'], ['visit', 'Visits'], ['record', 'Data-list rows'], ['order', 'Orders'],
        ['quotation', 'Quotations'], ['task', 'Tasks'], ['call', 'Calls'], ['ticket', 'Tickets'], ['lead', 'Leads'], ['deal', 'Deals'], ['project', 'Projects'], ['calendar', 'Calendar'], ['email', 'Emails']];
      const STATUSES: Array<[string, string]> = [['active', 'Active'], ['inProgress', 'In progress'], ['locked', 'Locked'], ['completed', 'Completed'], ['cancelled', 'Cancelled'], ['transferred', 'Transferred']];
      const PRIORITIES: Array<[string, string]> = [['low', 'Low'], ['normal', 'Normal'], ['high', 'High'], ['urgent', 'Urgent']];
      const filters = op !== 'assign' && op !== 'get';
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>What to do</Text>
            <View style={styles.chipRow}>
              {([['assign', 'Assign'], ['find', 'Find'], ['get', 'Get one'], ['update', 'Update'], ['transfer', 'Transfer'], ['complete', 'Complete'], ['cancel', 'Cancel'], ['remove', 'Remove (unassign)']] as Array<[string, string]>)
                .map(([id, label]) => chip(op === id, label, () => updateConfig({ asgOperation: id as any }), id))}
            </View>
            <Text style={styles.helperText}>Cancel and Remove end the assignment (it stays in the history); they notify differently, as in the app.</Text>
          </View>

          {op === 'assign' ? (
            <>
              <View style={styles.field}>
                <Text style={styles.label}>Assign</Text>
                <View style={styles.chipRow}>{KINDS.slice(0, 8).map(([id, label]) => chip((cfg.asgAssignKind || 'contact') === id, id === 'contact' ? 'This contact / contacts' : label, () => updateConfig({ asgAssignKind: id }), id))}</View>
              </View>
              {(cfg.asgAssignKind || 'contact') !== 'contact' || cfg.asgAssignTargetIds ? (
                <View style={styles.field}>
                  <InterpolatableInput label={`${KINDS.find(([k]) => k === (cfg.asgAssignKind || 'contact'))?.[1] || 'Item'} ids *`} value={cfg.asgAssignTargetIds || ''} size="sm" variables={availableVariables}
                    placeholder="e.g. {{order.id}} or {{found.orderIds}}" onChangeText={(text) => updateConfig({ asgAssignTargetIds: text || undefined })} />
                  {errors.asgAssignTargetIds && <Text style={styles.errorText}>{errors.asgAssignTargetIds}</Text>}
                </View>
              ) : (
                <TouchableOpacity onPress={() => updateConfig({ asgAssignTargetIds: '{{' })}><Text style={[styles.helperText, { color: colors.primary }]}>This run's contact — or assign contacts from a list (ids)…</Text></TouchableOpacity>
              )}
              {pickButton('To people', cfg.userIds?.length || 0, 'user', () => setAsgPicker('users'), errors.userIds)}
              {pickButton('And / or departments', cfg.departmentIds?.length || 0, 'department', () => setAsgPicker('departments'))}
              <View style={styles.field}>
                <InterpolatableInput label="Title" value={cfg.asgTitle || ''} size="sm" variables={availableVariables} placeholder="e.g. Follow up {{contact.name}}" onChangeText={(text) => updateConfig({ asgTitle: text || undefined })} />
              </View>
              <View style={styles.field}>
                <Text style={styles.label}>Priority</Text>
                <View style={styles.chipRow}>{PRIORITIES.map(([id, label]) => chip((cfg.asgPriority || 'normal') === id, label, () => updateConfig({ asgPriority: id as any }), id))}</View>
              </View>
              <View style={styles.field}>
                <Input label="Due in (hours)" value={cfg.asgDueInHours ? String(cfg.asgDueInHours) : ''} size="sm" keyboardType="decimal-pad" placeholder="No due time"
                  onChangeText={(text) => updateConfig({ asgDueInHours: Number(text.replace(/[^\d.]/g, '')) || undefined })} />
              </View>
              {(cfg.asgAssignKind || 'contact') === 'contact' ? (
                <View style={styles.chipRow}>
                  {chip(!!cfg.assignHandoff, 'AI agent hands over (ends its assignments first)', () => updateConfig({ assignHandoff: !cfg.assignHandoff }))}
                  {cfg.assignHandoff ? chip(!!cfg.assignPrevious, 'To who had the contact before', () => updateConfig({ assignPrevious: !cfg.assignPrevious })) : null}
                </View>
              ) : null}
            </>
          ) : null}

          {op === 'get' ? (
            <View style={styles.field}>
              <InterpolatableInput label="Assignment id *" value={cfg.asgId || ''} size="sm" variables={availableVariables} placeholder="e.g. {{assignments.first.id}}" onChangeText={(text) => updateConfig({ asgId: text || undefined })} />
              {errors.asgId && <Text style={styles.errorText}>{errors.asgId}</Text>}
            </View>
          ) : null}

          {filters ? (
            <>
              <View style={styles.field}>
                <Text style={styles.label}>Whose assignments</Text>
                <View style={styles.chipRow}>
                  {chip(scope === 'contact', "This run's contact", () => updateConfig({ asgScope: 'contact' }))}
                  {chip(scope === 'business', 'Across the business', () => updateConfig({ asgScope: 'business' }))}
                  {chip(scope === 'ids', 'Ids from the workflow', () => updateConfig({ asgScope: 'ids' }))}
                </View>
              </View>
              {scope === 'ids' ? (
                <View style={styles.field}>
                  <InterpolatableInput label="Assignment ids *" value={cfg.asgIds || ''} size="sm" variables={availableVariables} placeholder="e.g. {{assignments.ids}}" onChangeText={(text) => updateConfig({ asgIds: text || undefined })} />
                  {errors.asgIds && <Text style={styles.errorText}>{errors.asgIds}</Text>}
                </View>
              ) : null}
              {chips('About', KINDS, cfg.asgTargetKinds, (next) => updateConfig({ asgTargetKinds: next.length ? next : undefined }), 'None chosen = any kind.')}
              {chips('Status', STATUSES, cfg.asgStatuses, (next) => updateConfig({ asgStatuses: next.length ? next : undefined }), scope === 'ids' ? 'None chosen = any status.' : 'None chosen = open ones (active, in progress, locked).')}
              <View style={styles.field}>
                <Text style={styles.label}>Assigned to</Text>
                <View style={styles.chipRow}>
                  {([['any', 'Anyone'], ['users', 'Chosen people'], ['departments', 'Chosen departments'], ['nobody', 'Nobody']] as Array<[string, string]>)
                    .map(([id, label]) => chip((cfg.asgTo || 'any') === id, label, () => updateConfig({ asgTo: id as any }), id))}
                </View>
              </View>
              {cfg.asgTo === 'users' ? pickButton('People', cfg.userIds?.length || 0, 'user', () => setAsgPicker('users')) : null}
              {cfg.asgTo === 'departments' ? pickButton('Departments', cfg.departmentIds?.length || 0, 'department', () => setAsgPicker('departments')) : null}
              {chips('Priority', PRIORITIES, cfg.asgPriorities, (next) => updateConfig({ asgPriorities: next.length ? next : undefined }))}
              <View style={styles.chipRow}>{chip(!!cfg.asgOverdue, 'Overdue only', () => updateConfig({ asgOverdue: !cfg.asgOverdue || undefined }))}</View>
              <View style={styles.field}>
                <Input label="Created in the last (days)" value={cfg.asgCreatedWithinDays ? String(cfg.asgCreatedWithinDays) : ''} size="sm" keyboardType="number-pad" placeholder="Any time"
                  onChangeText={(text) => updateConfig({ asgCreatedWithinDays: parseInt(text.replace(/[^0-9]/g, ''), 10) || undefined })} />
              </View>
              {chips('Came from', [['manual', 'By hand'], ['bulk', 'Bulk'], ['flow', 'Workflows & AI'], ['channel_settings', 'Channel auto-assign'], ['webhook', 'API / webhook']], cfg.asgSources,
                (next) => updateConfig({ asgSources: next.length ? next : undefined }))}
              <View style={styles.field}>
                <Input label="Most assignments" value={cfg.asgLimit ? String(cfg.asgLimit) : ''} size="sm" keyboardType="number-pad" placeholder="100"
                  onChangeText={(text) => updateConfig({ asgLimit: Math.min(500, parseInt(text.replace(/[^0-9]/g, ''), 10) || 0) || undefined })} />
              </View>
            </>
          ) : null}

          {op === 'update' ? (
            <>
              <Text style={styles.label}>Change</Text>
              {errors.asgUpdate && <Text style={styles.errorText}>{errors.asgUpdate}</Text>}
              <View style={styles.chipRow}>{PRIORITIES.map(([id, label]) => chip(cfg.asgSetPriority === id, `Priority: ${label}`, () => updateConfig({ asgSetPriority: cfg.asgSetPriority === id ? undefined : id as any }), id))}</View>
              <View style={styles.chipRow}>{([['active', 'Active'], ['inProgress', 'In progress'], ['locked', 'Locked']] as Array<[string, string]>).map(([id, label]) => chip(cfg.asgSetStatus === id, `Status: ${label}`, () => updateConfig({ asgSetStatus: cfg.asgSetStatus === id ? undefined : id as any }), id))}</View>
              <View style={styles.field}>
                <Input label="Due in (hours from now)" value={cfg.asgSetDueInHours ? String(cfg.asgSetDueInHours) : ''} size="sm" keyboardType="decimal-pad" placeholder="Keep"
                  onChangeText={(text) => updateConfig({ asgSetDueInHours: Number(text.replace(/[^\d.]/g, '')) || undefined })} />
              </View>
              <View style={styles.field}>
                <InterpolatableInput label="Or due date" value={cfg.asgSetDueDate || ''} size="sm" variables={availableVariables} placeholder="e.g. 2026-11-01 17:00 or {{order.deliveryDate}}" onChangeText={(text) => updateConfig({ asgSetDueDate: text || undefined })} />
              </View>
              <View style={styles.field}>
                <InterpolatableInput label="Title" value={cfg.asgSetTitle || ''} size="sm" variables={availableVariables} placeholder="Keep" onChangeText={(text) => updateConfig({ asgSetTitle: text || undefined })} />
              </View>
              <View style={styles.field}>
                <InterpolatableInput label="Description" value={cfg.asgSetDescription || ''} size="sm" multiline variables={availableVariables} placeholder="Keep" onChangeText={(text) => updateConfig({ asgSetDescription: text || undefined })} />
              </View>
              <View style={styles.field}>
                <InterpolatableInput label="Add tags" value={cfg.asgAddTags || ''} size="sm" variables={availableVariables} placeholder="e.g. vip, follow-up" onChangeText={(text) => updateConfig({ asgAddTags: text || undefined })} />
              </View>
            </>
          ) : null}

          {op === 'transfer' ? (
            <>
              {pickButton('Transfer to a person', cfg.asgTransferUserId ? 1 : 0, 'user', () => setAsgPicker('toUser'), errors.asgTransfer)}
              {pickButton('Or to a department', cfg.asgTransferDepartmentId ? 1 : 0, 'department', () => setAsgPicker('toDepartment'))}
            </>
          ) : null}

          {op === 'transfer' || op === 'complete' ? (
            <View style={styles.field}>
              <InterpolatableInput label={op === 'complete' ? 'Completion note' : 'Reason'} value={cfg.asgNote || ''} size="sm" variables={availableVariables} placeholder="Optional" onChangeText={(text) => updateConfig({ asgNote: text || undefined })} />
            </View>
          ) : null}

          <View style={styles.field}>
            <Input label="Save result as" value={cfg.saveResultAs || ''} size="sm" placeholder="assignments" onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') || undefined })} />
            <Text style={styles.helperText}>{`{{${save}.count}}, {{${save}.ids}}, {{${save}.first.id}} — and the ids of what they are about: {{${save}.contactIds}}, {{${save}.visitIds}}, {{${save}.recordIds}}, {{${save}.orderIds}}…`}</Text>
          </View>
          {op !== 'assign' ? (
            <View style={styles.chipRow}>{chip(!!cfg.asgEmptyPath, 'A separate path when none are found', () => updateConfig({ asgEmptyPath: !cfg.asgEmptyPath || undefined }))}</View>
          ) : null}

          <BusinessUserPicker visible={asgPicker === 'users'} onClose={() => setAsgPicker(null)} mode="multiple" source="users" selectedUserIds={cfg.userIds || []}
            onConfirm={(ids) => { updateConfig({ userIds: ids }); setAsgPicker(null); }} />
          <BusinessUserPicker visible={asgPicker === 'departments'} onClose={() => setAsgPicker(null)} mode="multiple" source="departments" selectedUserIds={cfg.departmentIds || []}
            onConfirm={(ids) => { updateConfig({ departmentIds: ids }); setAsgPicker(null); }} />
          <BusinessUserPicker visible={asgPicker === 'toUser'} onClose={() => setAsgPicker(null)} mode="single" source="users" selectedUserIds={cfg.asgTransferUserId ? [cfg.asgTransferUserId] : []}
            onConfirm={(ids) => { updateConfig({ asgTransferUserId: ids[0], asgTransferDepartmentId: undefined }); setAsgPicker(null); }} />
          <BusinessUserPicker visible={asgPicker === 'toDepartment'} onClose={() => setAsgPicker(null)} mode="single" source="departments" selectedUserIds={cfg.asgTransferDepartmentId ? [cfg.asgTransferDepartmentId] : []}
            onConfirm={(ids) => { updateConfig({ asgTransferDepartmentId: ids[0], asgTransferUserId: undefined }); setAsgPicker(null); }} />

          {renderNextNodePicker(cfg.asgEmptyPath ? 'Then (found)' : 'Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
          {cfg.asgEmptyPath && op !== 'assign' ? renderNextNodePicker('None found', cfg.recordNotFoundNextNodeId, (id) => updateConfig({ recordNotFoundNextNodeId: id }), 'notfound') : null}
        </>
      );
    }

    case 'send_bulk_whatsapp': {
      const selected = waTemplates.find((t) => t._id === cfg.waTemplateId);
      const params = cfg.waTemplateParams || [];
      const count = Math.max(selected?.params || 0, params.length);
      const source = cfg.bulkSource || 'contacts';
      const chip = (on: boolean, label: string, onPress: () => void, key?: string) => (
        <TouchableOpacity key={key || label} style={[styles.chip, on && styles.actionTypeOptionActive]} onPress={onPress}>
          <Text style={[styles.helperText, { marginTop: 0 }, on && { color: colors.primary }]}>{label}</Text>
        </TouchableOpacity>
      );
      const select = (label: string, value: string | undefined, placeholder: string, onPress: () => void, error?: string, onClear?: () => void) => (
        <View style={styles.field}>
          <Text style={styles.label}>{label}</Text>
          <TouchableOpacity style={styles.selectButton} onPress={onPress}>
            <Text style={[styles.selectButtonText, !value && styles.selectButtonPlaceholder]} numberOfLines={1}>{value || placeholder}</Text>
            {value && onClear ? (
              <TouchableOpacity onPress={onClear} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}><Ionicons name="close-circle" size={18} color={colors.textLight} /></TouchableOpacity>
            ) : <Ionicons name="chevron-down" size={20} color={colors.textLight} />}
          </TouchableOpacity>
          {error ? <Text style={styles.errorText}>{error}</Text> : null}
        </View>
      );
      const columnName = (id?: string) => selectedListColumns.find((c) => c.id === id)?.name;
      // {{recipient.<field>}} the template values can use, by source.
      const recipientVars = source === 'data_list'
        ? selectedListColumns.map((c) => `{{recipient.${c.name.trim().toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_+|_+$/g, '')}}}`)
        : source === 'contacts' ? ['{{recipient.name}}', '{{recipient.phone}}', '{{recipient.email}}', '{{recipient.city}}'] : ['{{recipient.name}}', '{{recipient.phone}}', '{{recipient.<field>}}'];
      return (
        <>
          <Text style={styles.helperText}>Sends an approved template to many at once — like a WhatsApp campaign: within your plan's message quota, paced, each contact tagged with the batch. The workflow waits here until the batch is sent, then goes on (about a minute after it finishes).</Text>

          <View style={styles.field}>
            <Text style={styles.label}>Send to</Text>
            <View style={styles.chipRow}>
              {chip(source === 'contacts', 'Contacts (filter)', () => updateConfig({ bulkSource: 'contacts' }))}
              {chip(source === 'data_list', 'A data list', () => updateConfig({ bulkSource: 'data_list' }))}
              {chip(source === 'context', 'A list in this workflow', () => updateConfig({ bulkSource: 'context' }))}
            </View>
            {errors.bulkSource && <Text style={styles.errorText}>{errors.bulkSource}</Text>}
          </View>

          {source === 'contacts' ? (
            <>
              <View style={styles.field}>
                <Input label="With tags" value={(cfg.bulkTags || []).join(', ')} size="sm" placeholder="e.g. vip, riyadh"
                  onChangeText={(text) => updateConfig({ bulkTags: text.split(/[,،]/).map((x) => x.trim()).filter(Boolean) })} />
                <View style={styles.chipRow}>
                  {chip((cfg.bulkTagsMode || 'any') === 'any', 'Any of them', () => updateConfig({ bulkTagsMode: 'any' }))}
                  {chip(cfg.bulkTagsMode === 'all', 'All of them', () => updateConfig({ bulkTagsMode: 'all' }))}
                </View>
              </View>
              {select('In the contacts list', bulkContactLists.find((l) => l._id === cfg.bulkContactListId)?.name, 'Any', () => setBulkPicker('contactList'), undefined, () => updateConfig({ bulkContactListId: undefined }))}
              {select('Assigned to a person', cfg.bulkAssignedUserId ? 'Selected person' : undefined, 'Anyone', () => setBulkPicker('user'), undefined, () => updateConfig({ bulkAssignedUserId: undefined }))}
              {select('Or to a department', cfg.bulkAssignedDepartmentId ? 'Selected department' : undefined, 'Any', () => setBulkPicker('department'), undefined, () => updateConfig({ bulkAssignedDepartmentId: undefined }))}
              <View style={styles.field}>
                <Input label="City" value={cfg.bulkCity || ''} size="sm" placeholder="e.g. Riyadh" onChangeText={(text) => updateConfig({ bulkCity: text || undefined })} />
              </View>
              <View style={styles.field}>
                <Input label="Added in the last (days)" value={cfg.bulkCreatedWithinDays ? String(cfg.bulkCreatedWithinDays) : ''} size="sm" keyboardType="number-pad" placeholder="Any time"
                  onChangeText={(text) => updateConfig({ bulkCreatedWithinDays: parseInt(text.replace(/[^0-9]/g, ''), 10) || undefined })} />
              </View>
            </>
          ) : null}

          {source === 'data_list' ? (
            <>
              {select('Data list *', bulkLists.find((l) => l._id === cfg.bulkDataListId)?.name, 'Select a list', () => setBulkPicker('list'), errors.bulkDataListId)}
              {cfg.bulkDataListId ? (
                <>
                  {select('Phone column *', columnName(cfg.bulkPhoneColumnId), 'Which column holds the mobile number', () => setBulkPicker('phone'), errors.bulkPhoneColumnId)}
                  {select('Name column', columnName(cfg.bulkNameColumnId), 'Optional', () => setBulkPicker('name'), undefined, () => updateConfig({ bulkNameColumnId: undefined }))}
                </>
              ) : null}
            </>
          ) : null}

          {source === 'context' ? (
            <>
              <View style={styles.field}>
                <InterpolatableInput label="The list (variable) *" value={cfg.bulkContextPath || ''} size="sm" variables={availableVariables}
                  placeholder="e.g. webhook.items" onChangeText={(text) => updateConfig({ bulkContextPath: text.replace(/[{}\s]/g, '') || undefined })} />
                {errors.bulkContextPath && <Text style={styles.errorText}>{errors.bulkContextPath}</Text>}
                <Text style={styles.helperText}>A variable holding a list — each item a phone, or an object with one.</Text>
              </View>
              <View style={styles.field}>
                <Input label="Phone field in each item" value={cfg.bulkPhonePath || ''} size="sm" placeholder="phone (also tried: mobile, phoneNumber)" onChangeText={(text) => updateConfig({ bulkPhonePath: text.trim() || undefined })} />
              </View>
              <View style={styles.field}>
                <Input label="Name field in each item" value={cfg.bulkNamePath || ''} size="sm" placeholder="name" onChangeText={(text) => updateConfig({ bulkNamePath: text.trim() || undefined })} />
              </View>
            </>
          ) : null}

          <View style={styles.field}>
            <Text style={styles.label}>Template *</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setWaTemplatePickerOpen(true)}>
              <Text style={[styles.selectButtonText, !cfg.waTemplateName && styles.selectButtonPlaceholder]}>
                {cfg.waTemplateName ? `${cfg.waTemplateName} (${cfg.waTemplateLanguage})` : 'Select an approved template'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            {errors.waTemplateId && <Text style={styles.errorText}>{errors.waTemplateId}</Text>}
          </View>
          {renderSimpleListModal(waTemplatePickerOpen, () => setWaTemplatePickerOpen(false),
            waTemplates.map((t) => ({ id: t._id, label: `${t.templateName} (${t.language})${t.category ? ` · ${t.category}` : ''}` })),
            (id) => {
              const t = waTemplates.find((x) => x._id === id);
              updateConfig({ waTemplateId: id, waTemplateName: t?.templateName, waTemplateLanguage: t?.language, waTemplateParams: Array.from({ length: t?.params || 0 }, (_, i) => params[i] || ''), waHeaderMediaUrl: undefined, waHeaderFilename: undefined, waHeaderAsset: undefined });
            },
            loadingOptions, 'Select Template', 'No approved templates — submit one in Templates first.')}
          {selected?.headerMedia ? (
            <View style={styles.field}>
              <Text style={styles.label}>{`Header ${selected.headerMedia}`}</Text>
              {/* The conversation template picker's own file / place picker. */}
              <AssetCard
                headerType={selected.headerMedia as any}
                headerAsset={cfg.waHeaderAsset ? { ...(cfg.waHeaderAsset as any), ...(cfg.waHeaderAsset.type === 'location' ? { mediaId: JSON.stringify(cfg.waHeaderAsset) } : {}) } : undefined}
                campaignName="workflow"
                onAssetChange={(asset) => {
                  // Kept as a link (an upload's Meta media id expires) or the place itself.
                  if (asset.type === 'location') {
                    let place: any = {};
                    try { place = JSON.parse(asset.mediaId || '{}'); } catch { /* none */ }
                    updateConfig({ waHeaderAsset: { type: 'location', latitude: place.latitude, longitude: place.longitude, address: place.address } });
                  } else {
                    updateConfig({ waHeaderAsset: { type: asset.type, url: asset.url, ...(asset.filename ? { filename: asset.filename } : {}) } });
                  }
                }}
              />
              {cfg.waHeaderAsset ? (
                <TouchableOpacity onPress={() => updateConfig({ waHeaderAsset: undefined })}>
                  <Text style={[styles.helperText, { color: colors.primary }]}>Use the template's own instead</Text>
                </TouchableOpacity>
              ) : <Text style={styles.helperText}>{`Nothing chosen = the template's own ${selected.headerMedia}.`}</Text>}
              {selected.headerMedia !== 'location' ? (
                <InterpolatableInput label={`Or a link from the workflow`} value={cfg.waHeaderMediaUrl || ''} size="sm" variables={availableVariables}
                  placeholder="e.g. {{order.invoiceUrl}} — used first when set" onChangeText={(text) => updateConfig({ waHeaderMediaUrl: text.trim() || undefined })} />
              ) : null}
              {selected.headerMedia === 'document' ? (
                <InterpolatableInput label="Document file name" value={cfg.waHeaderFilename || ''} size="sm" variables={availableVariables}
                  placeholder="e.g. Invoice {{order.number}}.pdf" onChangeText={(text) => updateConfig({ waHeaderFilename: text || undefined })} />
              ) : null}
            </View>
          ) : null}
          {Array.from({ length: count }, (_, i) => (
            <View key={i} style={styles.field}>
              <InterpolatableInput label={`${({ header: 'Header', body: 'Body', button: 'Button link', footer: 'Footer' } as Record<string, string>)[selected?.sections?.[i + 1] || 'body'] || 'Value'} {{${i + 1}}}`} value={params[i] || ''}
                onChangeText={(text) => { const next = [...params]; next[i] = text; updateConfig({ waTemplateParams: next }); }}
                placeholder={i === 0 ? '{{recipient.name}}' : ''} size="sm" variables={availableVariables} />
            </View>
          ))}
          <Text style={styles.helperText}>Each recipient's own values: {recipientVars.slice(0, 8).join(' ')}</Text>
          <View style={styles.field}>
            <InterpolatableInput label="Text for open conversations" value={cfg.bulkMessage || ''} size="sm" multiline variables={availableVariables}
              placeholder="Optional — sent instead of the template to contacts who wrote in the last 24 hours, e.g. Hi {{recipient.name}}…"
              onChangeText={(text) => updateConfig({ bulkMessage: text.slice(0, 2000) || undefined })} />
          </View>
          <View style={styles.field}>
            <Input label="Most recipients" value={cfg.bulkMax ? String(cfg.bulkMax) : ''} size="sm" keyboardType="number-pad" placeholder="1000"
              onChangeText={(text) => updateConfig({ bulkMax: Math.min(5000, parseInt(text.replace(/[^0-9]/g, ''), 10) || 0) || undefined })} />
          </View>
          <View style={styles.field}>
            <Input label="Save result as" value={cfg.saveResultAs || ''} size="sm" placeholder="bulk" onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') || undefined })} />
            <Text style={styles.helperText}>Then: {`{{${cfg.saveResultAs || 'bulk'}.sent}}`}, {`{{${cfg.saveResultAs || 'bulk'}.failed}}`}, {`{{${cfg.saveResultAs || 'bulk'}.skipped}}`} (no mobile number)</Text>
          </View>

          {renderSimpleListModal(bulkPicker === 'list', () => setBulkPicker(null),
            bulkLists.map((l) => ({ id: l._id, label: `${l.name} · ${l.kind}` })),
            (id) => updateConfig({ bulkDataListId: id, bulkPhoneColumnId: undefined, bulkNameColumnId: undefined }), loadingOptions, 'Select Data List', 'No data lists yet.')}
          {renderSimpleListModal(bulkPicker === 'contactList', () => setBulkPicker(null),
            bulkContactLists.map((l) => ({ id: l._id, label: l.name })), (id) => updateConfig({ bulkContactListId: id }), loadingOptions, 'Select Contacts List', 'No contacts lists yet.')}
          {renderSimpleListModal(bulkPicker === 'phone' || bulkPicker === 'name', () => setBulkPicker(null),
            selectedListColumns.map((c) => ({ id: c.id, label: `${c.name} (${c.type})` })),
            (id) => updateConfig(bulkPicker === 'phone' ? { bulkPhoneColumnId: id } : { bulkNameColumnId: id }), loadingOptions, 'Select Column', 'This list has no columns.')}
          <BusinessUserPicker visible={bulkPicker === 'user'} onClose={() => setBulkPicker(null)} mode="single" source="users"
            selectedUserIds={cfg.bulkAssignedUserId ? [cfg.bulkAssignedUserId] : []}
            onConfirm={(ids) => { updateConfig({ bulkAssignedUserId: ids[0], bulkAssignedDepartmentId: undefined }); setBulkPicker(null); }} />
          <BusinessUserPicker visible={bulkPicker === 'department'} onClose={() => setBulkPicker(null)} mode="single" source="departments"
            selectedUserIds={cfg.bulkAssignedDepartmentId ? [cfg.bulkAssignedDepartmentId] : []}
            onConfirm={(ids) => { updateConfig({ bulkAssignedDepartmentId: ids[0], bulkAssignedUserId: undefined }); setBulkPicker(null); }} />
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );
    }

    case 'find_leads':
      return (
        <>
          <View style={styles.field}>
            <InterpolatableInput label="Who to find *" value={cfg.leadBrief || ''} onChangeText={(text) => updateConfig({ leadBrief: text.slice(0, 1500) })}
              placeholder="e.g. Cafés and restaurants in {{contact.city}} that may need disposable cups" size="sm" multiline variables={availableVariables} />
            {errors.leadBrief && <Text style={styles.errorText}>{errors.leadBrief}</Text>}
            <Text style={styles.helperText}>The AI turns this into a Lead Finder search (keywords, map categories, area) and it runs in the background within your plan's limits. Results appear in Lead Finder for review.</Text>
          </View>
          <View style={styles.field}>
            <Input label="Most results" value={cfg.leadLimit ? String(cfg.leadLimit) : ''} onChangeText={(text) => updateConfig({ leadLimit: Math.min(1000, parseInt(text.replace(/[^0-9]/g, ''), 10) || 0) || undefined })}
              placeholder="100" size="sm" keyboardType="number-pad" />
          </View>
          <View style={styles.field}>
            <Text style={styles.label}>Sources</Text>
            <View style={styles.chipRow}>
              {[{ id: 'map', label: 'Dislix map' }, { id: 'web', label: 'Web' }, { id: 'google_maps', label: 'Google Maps' }].map((o) => {
                const chosen = cfg.leadSources?.length ? cfg.leadSources : ['map'];
                const on = chosen.includes(o.id);
                return (
                  <TouchableOpacity key={o.id} style={[styles.chip, on && styles.actionTypeOptionActive]}
                    onPress={() => updateConfig({ leadSources: on ? chosen.filter((s) => s !== o.id) : [...chosen, o.id] })}>
                    <Text style={[styles.helperText, { marginTop: 0 }, on && { color: colors.primary }]}>{o.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'notify_team':
      return (
        <>
          <View style={styles.field}>
            <Text style={styles.label}>Who</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setUserPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !(cfg.userIds?.length) && styles.selectButtonPlaceholder]}>
                {cfg.userIds?.length ? `${cfg.userIds.length} user${cfg.userIds.length === 1 ? '' : 's'} selected` : 'Owners and admins'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
          </View>
          <BusinessUserPicker visible={userPickerOpen} onClose={() => setUserPickerOpen(false)}
            onConfirm={(ids) => { updateConfig({ userIds: ids }); setUserPickerOpen(false); }} mode="multiple" source="users" selectedUserIds={cfg.userIds || []} />
          <View style={styles.field}>
            <InterpolatableInput label="Title" value={cfg.notifyTitle || ''} onChangeText={(text) => updateConfig({ notifyTitle: text })}
              placeholder="e.g. New AI order {{order.number}}" size="sm" variables={availableVariables} />
          </View>
          <View style={styles.field}>
            <InterpolatableInput label="Message" value={cfg.notifyBody || ''} onChangeText={(text) => updateConfig({ notifyBody: text })}
              placeholder="e.g. {{contact.name}}: {{agent.summary}}" size="sm" variables={availableVariables} />
            <Text style={styles.helperText}>Tapping the alert opens this conversation.</Text>
          </View>
          {renderNextNodePicker('Then', cfg.nextNodeId, (nextNodeId) => updateConfig({ nextNodeId }), 'default')}
        </>
      );

    case 'check_time': {
      const routes = cfg.timeRoutes || [];
      const setRoute = (id: string, patch: Partial<NonNullable<FlowNodeConfig['timeRoutes']>[number]>) =>
        updateConfig({ timeRoutes: routes.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label, value) => ({ value, label }));
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((label, i) => ({ value: i + 1, label }));
      const time = (text: string) => text.replace(/[^0-9:]/g, '').slice(0, 5);
      return (
        <>
          <Text style={styles.helperText}>Uses your business timezone and working hours (Settings → Business profile). Rules are checked in order; the first that matches wins. Inside a rule, every condition you set must match.</Text>
          <View style={styles.field}>
            <View style={styles.buttonsHeader}>
              <Text style={styles.label}>Rules</Text>
              <TouchableOpacity accessibilityLabel="Add rule" onPress={() => updateConfig({ timeRoutes: [...routes, { id: genId('time'), label: '' }] })}>
                <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
            </View>
            {errors.timeRoutes && <Text style={styles.errorText}>{errors.timeRoutes}</Text>}
            {routes.map((route, index) => (
              <View key={route.id} style={styles.buttonRow}>
                <View style={styles.buttonRowTop}>
                  <View style={{ flex: 1 }}>
                    <Input value={route.label || ''} onChangeText={(text) => setRoute(route.id, { label: text.slice(0, 40) })} placeholder={`Rule ${index + 1} name, e.g. Working hours`} size="sm" />
                  </View>
                  <TouchableOpacity accessibilityLabel="Remove rule" onPress={() => updateConfig({ timeRoutes: routes.filter((r) => r.id !== route.id) })} style={styles.removeButtonIcon}>
                    <Ionicons name="close-circle" size={20} color={colors.error} />
                  </TouchableOpacity>
                </View>
                {errors[`time_${index}`] && <Text style={styles.errorText}>{errors[`time_${index}`]}</Text>}
                <Text style={[styles.label, { marginTop: spacing.sm }]}>Business hours</Text>
                {renderChoice([{ value: 'any', label: 'Any time' }, { value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }], route.businessHours || 'any',
                  (value) => setRoute(route.id, { businessHours: value === 'any' ? undefined : value }))}
                <Text style={styles.label}>Days of the week</Text>
                {renderChips(days, route.daysOfWeek, (next) => setRoute(route.id, { daysOfWeek: next.length ? next : undefined }))}
                <View style={styles.buttonRowTop}>
                  <View style={{ flex: 1 }}>
                    <Input label="From (HH:mm)" value={route.timeFrom || ''} onChangeText={(text) => setRoute(route.id, { timeFrom: time(text) || undefined })} placeholder="09:00" size="sm" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Input label="To (HH:mm)" value={route.timeTo || ''} onChangeText={(text) => setRoute(route.id, { timeTo: time(text) || undefined })} placeholder="17:00" size="sm" />
                  </View>
                </View>
                <Text style={styles.helperText}>A range past midnight works too, e.g. 22:00 → 06:00.</Text>
                <Input
                  label="Days of the month"
                  value={(route.daysOfMonth || []).map((d) => (d === -1 ? 'last' : String(d))).join(', ')}
                  onChangeText={(text) => {
                    const values = text.split(/[ ,]+/).map((v) => (v.trim().toLowerCase() === 'last' ? -1 : parseInt(v, 10))).filter((v) => v === -1 || (v >= 1 && v <= 31));
                    setRoute(route.id, { daysOfMonth: values.length ? [...new Set(values)] : undefined });
                  }}
                  placeholder="e.g. 1, 15, last"
                  size="sm"
                />
                <Text style={styles.label}>Months</Text>
                {renderChips(months, route.months, (next) => setRoute(route.id, { months: next.length ? next : undefined }))}
                <View style={styles.buttonRowTop}>
                  <View style={{ flex: 1 }}>
                    <Input label="From date" value={route.dateFrom || ''} onChangeText={(text) => setRoute(route.id, { dateFrom: text.replace(/[^0-9-]/g, '').slice(0, 10) || undefined })} placeholder="YYYY-MM-DD" size="sm" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Input label="To date" value={route.dateTo || ''} onChangeText={(text) => setRoute(route.id, { dateTo: text.replace(/[^0-9-]/g, '').slice(0, 10) || undefined })} placeholder="YYYY-MM-DD" size="sm" />
                  </View>
                </View>
                {renderNextNodePicker('Then', route.nextNodeId, (nextNodeId) => setRoute(route.id, { nextNodeId }), `time-next:${route.id}`)}
              </View>
            ))}
          </View>
          {renderNextNodePicker('Otherwise', cfg.timeDefaultNextNodeId, (nextNodeId) => updateConfig({ timeDefaultNextNodeId: nextNodeId }), 'time-default')}
          <View style={styles.field}>
            <Input
              label="Save time as"
              value={cfg.saveResultAs ?? 'now'}
              onChangeText={(text) => updateConfig({ saveResultAs: text.replace(/[^a-zA-Z0-9_]/g, '') })}
              placeholder="now"
              size="sm"
            />
            <Text style={styles.helperText}>{'Later steps can use {{' + (cfg.saveResultAs || 'now') + '.time}}, .date, .dayName, .isBusinessOpen…'}</Text>
          </View>
        </>
      );
    }

    case 'end':
      return <Text style={styles.helperText}>The flow ends here. No further checkers run for this contact.</Text>;

    default:
      return null;
  }
};

// A picker modal. Rendered as its own component: it uses a hook, and the node form shows a
// different set of pickers per node type — calling it as a plain function changed the form's
// hook count between node types (React error #310 on Show Product / Show Catalog).
function renderSimpleListModal(
  visible: boolean,
  onClose: () => void,
  items: Array<{ id: string; label: string }>,
  onPick: (id: string) => void,
  loading: boolean,
  title = 'Select',
  emptyText = 'Nothing found'
) {
  return <SimpleListModal visible={visible} onClose={onClose} items={items} onPick={onPick} loading={loading} title={title} emptyText={emptyText} />;
}

function SimpleListModal({ visible, onClose, items, onPick, loading, title, emptyText }: {
  visible: boolean;
  onClose: () => void;
  items: Array<{ id: string; label: string }>;
  onPick: (id: string) => void;
  loading: boolean;
  title: string;
  emptyText: string;
}) {
  const styles = useThemedStyles(getStyles);
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{title}</Text>
            <TouchableOpacity onPress={onClose}>
              <Ionicons name="close" size={24} color={colors.textDark} />
            </TouchableOpacity>
          </View>
          {loading ? (
            <View style={styles.emptyContainer}><ActivityIndicator size="large" color={colors.primary} /></View>
          ) : (
            <FlatList
              data={items}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity style={styles.option} onPress={() => { onPick(item.id); onClose(); }}>
                  <Text style={styles.optionLabel}>{item.label}</Text>
                </TouchableOpacity>
              )}
              ListEmptyComponent={<View style={styles.emptyContainer}><Text style={styles.emptyText}>{emptyText}</Text></View>}
            />
          )}
        </View>
      </View>
    </Modal>
  );
}

const getStyles = () => StyleSheet.create({
  field: { marginBottom: spacing.md },
  label: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold, marginBottom: spacing.xs },
  helperText: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.primary, marginTop: spacing.xs / 2 },
  errorText: { fontSize: typography.fontSize.xs, color: colors.error, fontFamily: typography.fontFamily.primary, marginTop: spacing.xs / 2 },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
  },
  selectButtonText: { fontSize: typography.fontSize.base, color: colors.textDark, fontFamily: typography.fontFamily.primary },
  selectButtonPlaceholder: { color: colors.textLight },
  buttonsHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  referenceBox: {
    marginTop: spacing.xs,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.lightGray,
  },
  referenceGroupLabel: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.textLight, textTransform: 'uppercase', marginTop: spacing.xs },
  referenceEntry: { fontSize: typography.fontSize.xs, color: colors.textDark, fontFamily: typography.fontFamily.primary, marginTop: 2 },
  buttonRow: {
    marginTop: spacing.sm,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.lightGray,
  },
  buttonRowTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  removeButtonIcon: { padding: spacing.xs / 2 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs, marginBottom: spacing.sm },
  agentHint: { flexDirection: 'row', gap: spacing.sm, padding: spacing.sm, marginBottom: spacing.md, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.primary, backgroundColor: `${String(colors.primary)}0D` },
  agentHintTitle: { fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.semibold, marginBottom: 2 },
  agentHintButton: { alignSelf: 'flex-start', marginTop: spacing.xs, paddingHorizontal: spacing.sm, paddingVertical: 6, borderRadius: designTokens.borderRadius.sm, backgroundColor: colors.primary },
  agentHintButtonText: { fontSize: typography.fontSize.xs, color: colors.white, fontFamily: typography.fontFamily.semibold },
  addButtonRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.xs, marginBottom: spacing.sm },
  addButtonRowText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  chip: {
    paddingVertical: spacing.xs / 2,
    paddingHorizontal: spacing.sm,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  actionTypeRow: { flexDirection: 'row', gap: spacing.xs, marginTop: spacing.sm, marginBottom: spacing.xs },
  actionTypeOption: {
    flex: 1,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    alignItems: 'center',
  },
  actionTypeOptionActive: { borderColor: colors.primary, backgroundColor: colors.primaryTransparent },
  actionTypeOptionDisabled: { opacity: 0.4 },
  actionTypeOptionText: { fontSize: typography.fontSize.xs, color: colors.textDark, fontFamily: typography.fontFamily.primary },
  actionTypeOptionTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.lg,
    borderTopRightRadius: designTokens.borderRadius.lg,
    maxHeight: '80%',
    paddingBottom: spacing.lg,
  },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.lg, borderBottomWidth: 1, borderBottomColor: colors.border },
  modalTitle: { fontSize: typography.fontSize.xl, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold },
  option: { padding: spacing.md, borderBottomWidth: 1, borderBottomColor: `${String(colors.border)}40` },
  optionLabel: { fontSize: typography.fontSize.base, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  emptyContainer: { padding: spacing.xl, alignItems: 'center', justifyContent: 'center' },
  emptyText: { fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.primary },
});
